// Ein Weg zur API. Die Sitzung steckt im Cookie, deshalb nur credentials.

import { vergissAlles } from './teile/merkplatz.ts';
import { antwortLesen } from './teile/wartung.ts';
import { meldeAenderung } from './teile/frisch.ts';
import {
  kwJahrVon, kwVon, plusTage, wochentagNr,
} from './teile/rastertage.ts';
import { fragen, melden } from './teile/rueckfrage.tsx';

export interface Anmelder {
  benutzerId: string;
  name: string;
  email: string | null;
  rechte: string[];
  istSub: boolean;
  firma: string | null;
  /** Gesetzt, wenn gerade jemand in diesen Benutzer hineinversetzt ist. */
  echterBenutzerId?: string;
  echterName?: string;
  /** true = Passwort stimmte, der zweite Faktor fehlt noch. */
  faktorOffen?: boolean;
  /**
   * true = der Innendienst hat dieses Passwort gesetzt. Bis ein eigenes
   * steht, geht nichts anderes - der Riegel sitzt im Server (Migration 054).
   */
  passwortWechseln?: boolean;
  /**
   * true = dieser Zugang wartet auf seine Freischaltung (Migration 130).
   *
   * Er ist richtig angemeldet und darf trotzdem nichts ausser seinem
   * Passwort und seinem zweiten Faktor. Der Riegel sitzt im Server vor jeder
   * Anfrage; das hier ist nur die Anzeige dazu.
   */
  wartet?: boolean;
  /** Seit wann er wartet. */
  wartetSeit?: string | null;
  /**
   * true = die Gruppe dieses Zugangs verlangt einen zweiten Faktor, und er
   * hat keinen (Migration 217).
   *
   * Er ist richtig angemeldet und darf trotzdem nichts ausser dem Einrichten.
   * Der Riegel sitzt im Server vor jeder Anfrage; das hier ist nur die
   * Anzeige dazu. Bis .148 gab es ihn nicht: die Pflicht war eine
   * Aufforderung nach dem Anmelden, und wer sie wegklickte, arbeitete weiter.
   */
  faktorEinrichtenNoetig?: boolean;
  /**
   * true = für diesen Zugang liegt ein Profilbild. Nur das, nicht die
   * Kennung: die Anzeige braucht die Antwort auf „Bild oder Initialen".
   * Sie darf es NICHT am Ladefehler eines Bildes erkennen - das schreibt bei
   * jedem Seitenaufruf einen erwarteten Fehler in die Browserkonsole.
   */
  hatBild?: boolean;
  sitzungen?: { id: string; geraet: string | null; angelegt_am: string }[];
  /** Die Bereiche dieses Zugangs (.333): ne3, ne4 oder beide. Zwei = Umschalter. */
  bereiche?: Bereich[];
}

export interface Bereich { schluessel: string; name: string; kurz: string; untertitel: string; farbe: string }

/**
 * DIE EINE STELLE, DIE SAGT, DASS SICH ETWAS GEÄNDERT HAT.
 *
 *   ORIGINALTEXT MARVIN (09.09.2026)
 *     „Überall müssen alle Änderungen live gepusht werden."
 *
 * Jeder Aufruf dieses Hauses geht durch die fünf Helfer darunter. Ein
 * SCHREIBENDER meldet sich hier, und jede Stelle, die Daten anzeigt, zieht
 * nach (`teile/frisch.ts`, `teile/Frisch.tsx`). Vorher entschied jede
 * Aufrufstelle eines Fensters für sich, ob der Bildschirm dahinter etwas
 * davon erfährt - **und eine Regel, die an dreissig Aufrufstellen stehen
 * muss, wird bei der einunddreissigsten vergessen.**
 *
 * GEMELDET WIRD NUR NACH EINER ERFOLGREICHEN ANTWORT. Was abgewiesen wurde,
 * hat nichts geändert; ein Nachzug darauf holte denselben Stand noch einmal
 * und liesse den roten Kasten daneben wie einen Fehler des Bildschirms
 * aussehen.
 */
function melde(pfad: string, opt: RequestInit): void {
  meldeAenderung({ weg: pfad, art: String(opt.method ?? 'GET') });
}

async function ruf<T>(pfad: string, opt: RequestInit = {}): Promise<T> {
  const a = await fetch('/api' + pfad, {
    credentials: 'same-origin',
    headers: opt.body ? { 'content-type': 'application/json' } : undefined,
    ...opt,
  });
  const text = await a.text();
  /* Wartungsmodus und Fehlerseiten ohne JSON (.461), siehe teile/wartung.ts. */
  const daten = antwortLesen(a.status, text);
  if (!a.ok) throw Object.assign(new Error(daten?.fehler ?? a.statusText), { daten });
  melde(pfad, opt);
  return daten as T;
}

/**
 * Eine Antwort, die keine JSON ist: eine PDF-Vorschau.
 *
 * Sie wird NICHT abgelegt, sie existiert nur so lange, wie das Fenster offen
 * ist. Deshalb kommt eine Blob-Adresse zurück und keine Datei-Kennung. Wer
 * sie schliesst, gibt sie mit `URL.revokeObjectURL` wieder frei; sonst hängt
 * das Papier bis zum Neuladen der Seite im Speicher.
 */
async function rufRoh(pfad: string, opt: RequestInit = {}): Promise<string> {
  const a = await fetch('/api' + pfad, {
    credentials: 'same-origin',
    headers: opt.body ? { 'content-type': 'application/json' } : undefined,
    ...opt,
  });
  if (!a.ok) {
    // Der Fehlerfall antwortet mit JSON, der Erfolgsfall mit PDF.
    const t = await a.text();
    const d: any = antwortLesen(a.status, t);
    throw Object.assign(new Error(d?.fehler ?? a.statusText), { daten: d });
  }
  melde(pfad, opt);
  return URL.createObjectURL(await a.blob());
}

/**
 * Dateien hochladen.
 *
 * Der Inhaltstyp wird NICHT gesetzt: der Browser muss ihn selbst schreiben,
 * weil er die Trennmarke zwischen den Teilen enthält. Wer hier
 * "multipart/form-data" von Hand einträgt, bekommt eine Sendung ohne Marke,
 * und der Server findet keine einzige Datei darin.
 */
async function sendeDateien<T>(weg: string, dateien: File[]): Promise<T> {
  const paket = new FormData();
  for (const d of dateien) paket.append('datei', d, d.name);
  const a = await fetch('/api' + weg, { method: 'POST', body: paket });
  const t = await a.text();
  const d = antwortLesen(a.status, t) ?? {};
  if (!a.ok) {
    const f: any = new Error(d.fehler ?? 'Das hat nicht geklappt.');
    f.daten = d; throw f;
  }
  melde(weg, { method: 'POST' });
  return d as T;
}

/** Eine Datei mit Feldern daneben. Die Felder gehen VOR der Datei hinaus: der
 *  Server liest die Sendung der Reihe nach, und die Art muss dastehen, bevor
 *  er die Datei liest. */
async function sendeDateiMitFeldern<T>(weg: string, datei: File, felder: Record<string, string>): Promise<T> {
  const paket = new FormData();
  for (const [k, w] of Object.entries(felder)) paket.append(k, w);
  paket.append('datei', datei, datei.name);
  const a = await fetch('/api' + weg, { method: 'POST', body: paket });
  const t = await a.text();
  const d = t ? JSON.parse(t) : {};
  if (!a.ok) {
    const f: any = new Error(d.fehler ?? 'Das hat nicht geklappt.');
    f.daten = d; throw f;
  }
  melde(weg, { method: 'POST' });
  return d as T;
}

/**
 * Ein Paket eines ganzen Ordnerbaums, DATEI UND PFAD.
 *
 * Der Pfad geht als eigenes Feld VOR jeder Datei hinaus, in derselben
 * Reihenfolge. Der Server stellt beide Listen wieder gegeneinander; gehen die
 * Zahlen auseinander, lehnt er ab, statt zu raten. Ein Bild im falschen
 * Ordner ist schlimmer als ein abgelehntes Paket: es fällt niemandem auf.
 */
async function sendeBaum<T>(weg: string,
                            stuecke: { datei: File; pfad: string }[],
                            felder: Record<string, string> = {}): Promise<T> {
  const paket = new FormData();
  // Die Felder VOR den Dateien: der Server liest der Reihe nach.
  for (const [k, v] of Object.entries(felder)) paket.append(k, v);
  for (const s of stuecke) {
    paket.append('pfad', s.pfad);
    paket.append('datei', s.datei, s.datei.name);
  }
  const a = await fetch('/api' + weg, { method: 'POST', body: paket });
  const t = await a.text();
  const d = t ? JSON.parse(t) : {};
  if (!a.ok) {
    const f: any = new Error(d.fehler ?? 'Das hat nicht geklappt.');
    f.daten = d; throw f;
  }
  melde(weg, { method: 'POST' });
  return d as T;
}

/**
 * Die Auswahl von Hand an der Rotberichtigung.
 *
 * `ohne` sind Dateibezüge, die NICHT mitgehen; `dazu` sind welche, die die
 * Erkennung übersehen hat, mit dem Kapitel, in das sie gehören. Beides sind
 * Kennungen aus `datei_bezug` - der Server prüft sie gegen den Auftrag und
 * weist eine fremde ab.
 */
export interface RbAuswahl {
  ohne: string[];
  dazu: { bezugId: string; kapitel: string }[];
}

/** Der gewählte Bereich, wie App.tsx ihn merkt; ohne Speicher Netzebene 3. */
export function bereichJetzt(): string {
  try { return localStorage.getItem('mds.bereich') || 'ne3'; } catch { return 'ne3'; }
}

export const api = {
  fahrtroute: (punkte: { lat: number; lon: number }[], rund: boolean) => ruf<any>('/fahrtroute', { method: 'POST', body: JSON.stringify({ punkte, rund }) }),
  ich: () => ruf<Anmelder>('/ich'),
  anmelden: (email: string, passwort: string) =>
    ruf<{ ok: boolean; faktorOffen: boolean; faktorEinrichten: boolean }>(
      '/anmelden', { method: 'POST', body: JSON.stringify({ email, passwort }) }),
  /* BEIM ABMELDEN VERGISST DER MERKPLATZ ALLES (.398): der Nächste am selben Browser sieht nichts vom Vorigen. */
  abmelden: () => ruf('/abmelden', { method: 'POST' }).finally(() => vergissAlles()),
  /* EINE EINZELNE ANMELDUNG BEENDEN, nicht alle. „Passwort ändern" ist die
     grobe Axt (alle anderen Sitzungen enden), das hier ist der Handgriff für
     das eine Tablet, das im Büro liegt. Die AKTUELLE weist der Server ab: wer
     sie beendet, sperrt sich selbst aus. */
  sitzungBeenden: (id: string) =>
    ruf<{ ok: boolean; geraet: string | null }>(
      '/ich/sitzung/' + id, { method: 'DELETE' }),
  auftraege: (p: URLSearchParams) => ruf<any>('/auftraege?' + p.toString()),
  auftrag: (id: string) => ruf<any>('/auftraege/' + id),
  zustand: () => ruf<any>('/zustand'),
  /* WAS NOCH FEHLT, DAMIT n8n UND TELEGRAM TRAGEN.
     Marvin, 01.09.2026: „wie müssen jetzt sauber N8N und telegram anbinden.
     was ist zu tun?" Die Antwort gehört ins System und nicht in eine
     Anleitung: eine Anleitung weiss nicht, was hier schon steht. */
  anbindung: () => ruf<any>('/anbindung'),
  /* UND JETZT WIRKLICH AUSPROBIEREN (05.09.2026). „steht" sagt, dass ein
     Schlüssel gefüllt ist; ob eine Nachricht ankommt, sagt nur der Versuch. */
  anbindungPruefen: (was: string) =>
    ruf<any>('/anbindung/pruefen',
             { method: 'POST', body: JSON.stringify({ was }) }),

  /* DIE SCHLÜSSEL FÜR FREMDE SYSTEME (Migration 167).
     Marvin, 06.09.2026: „damit man die daten dort hochladen und eintragen
     kann, aber auch auslesen." Der Klartext kommt GENAU EINMAL zurück, beim
     Anlegen; es gibt keinen Weg, der ihn ein zweites Mal herausgibt. */
  apiSchluessel: () => ruf<any>('/schnittstelle/schluessel'),
  apiWege: () => ruf<any>('/schnittstelle/wege'),
  /* DIE ADRESSE FÜR DIE ANLEITUNG. Sie kommt vom Server, weil nur er weiss,
     unter welchem Namen er gerade erreichbar ist - `location.origin` wäre
     dieselbe Angabe aus zweiter Hand und bei einem Proxy davor die falsche. */
  apiAdresse: () => ruf<{ basis: string }>('/schnittstelle/adresse'),
  /* DIE KANÄLE, AUF DENEN EINE DATEI HEREINKOMMT (Punkt 4b, 08.09.2026).
     Ob ein Kanal einem fremden System offensteht, rechnet der SERVER aus
     derselben Sperrliste wie `/ich/wege` - hier stünde sonst eine zweite
     Fassung, und die wäre beim nächsten Eintrag die falsche. */
  apiKanaele: () => ruf<any>('/schnittstelle/kanaele'),
  /* WAS GENAU DIESER SCHLÜSSEL DARF - gerechnet auf dem SERVER. Die
     Oberfläche hat die Rechte und das Verzeichnis und könnte es selbst
     nebeneinanderlegen; dann stünde dieselbe Rechnung zweimal da, und die
     Sperrliste, die nur für einen Schlüssel gilt, wäre die erste, die dabei
     vergessen wird. */
  apiSchluesselWege: (id: string) =>
    ruf<any>(`/schnittstelle/schluessel/${id}/wege`),
  /* MIT IHREN RECHTEN, und aus einem eigenen Weg: `GET /benutzer` verlangt
     `benutzer.verwalten`, und wer nur die Schnittstelle verwaltet, stünde
     sonst vor einem leeren Auswahlfeld. */
  apiZugaenge: () => ruf<any>('/schnittstelle/zugaenge'),
  apiSchluesselAnlegen: (daten: Record<string, unknown>) =>
    ruf<any>('/schnittstelle/schluessel',
             { method: 'POST', body: JSON.stringify(daten) }),
  apiSchluesselSperre: (id: string, gesperrt: boolean, grund: string) =>
    ruf<any>('/schnittstelle/schluessel/' + id + '/sperre',
             { method: 'PUT', body: JSON.stringify({ gesperrt, grund }) }),
  apiSchluesselLoeschen: (id: string, grund: string) =>
    ruf<any>('/schnittstelle/schluessel/' + id
             + '?grund=' + encodeURIComponent(grund), { method: 'DELETE' }),

  /* Das Änderungsprotokoll (Migration 101).
     Es gab den Weg seit Migration 001 und keinen Bildschirm - eine Spur, die
     niemand lesen kann, ist keine Spur, sondern eine Beruhigung. */
  /* .544: im Bereich NE4 nur die Änderungen an NE4. */
  protokoll: (f: Record<string, string> = {}) =>
    ruf<any>('/protokoll?' + new URLSearchParams({ ...f, bereich: bereichJetzt() }).toString()),
  gemeinden: () => ruf<any[]>('/gemeinden'),
  /* EINE PROBE UND EIN LAUF, EIN WEG. `probe` schreibt nichts und sagt je
     Gemeinde, was sie hält; ohne `probe` wird gelöscht, und der Grund ist
     Pflicht. Zwei Wege über dieselbe Frage laufen beim ersten Nachtrag
     auseinander. */
  gemeindenLoeschprobe: (ids: string[]) =>
    ruf<any>('/gemeinden/loeschen', {
      method: 'POST', body: JSON.stringify({ ids, probe: true }) }),
  gemeindenLoeschen: (ids: string[], grund: string) =>
    ruf<any>('/gemeinden/loeschen', {
      method: 'POST', body: JSON.stringify({ ids, grund }) }),
  subunternehmer: () => ruf<any[]>('/subunternehmer'),
  /* PREISE JE PARTNER (Migration 260). Was ein Partner für eine Position
     bekommt - nicht zu verwechseln mit dem Rahmenvertragspreis, der sagt,
     was WIR bekommen. */
  subPreise: (firma: string) =>
    ruf<any>(`/subunternehmer/${firma}/preise`),
  /* DIE ANDERE RICHTUNG (18.09.2026). Marvin: „wo weise ich die position denn
     dem Partner zu?" `subPreise` fragt von der FIRMA aus, `lvFirmen` von der
     POSITION aus - dieselbe Tabelle, 2 Fragen. Geschrieben wird weiter mit
     `subPreisSetzen`: 2 Schreibwege waeren 2 Regeln darueber, wie ein Preis
     abgeloest wird. */
  lvFirmen: (positionId: string) => ruf<any>(`/lv/${positionId}/firmen`),
  subPreisSetzen: (firma: string, b: Record<string, unknown>) =>
    ruf<any>(`/subunternehmer/${firma}/preise`,
             { method: 'PUT', body: JSON.stringify(b) }),
  /* WARUM SIEHT EIN PARTNER DIESEN AUFTRAG NICHT? Siehe
     `routen/schnittstelle.ts`; die Antwort nennt je Auftrag die Gründe. */
  apiSichtbarkeit: (suche: string, firma?: string) => {
    const p = new URLSearchParams({ suche });
    if (firma) p.set('firma', firma);
    return ruf<any>('/schnittstelle/sichtbarkeit?' + p.toString());
  },
  lv: () => ruf<any[]>('/lv'),
  // Alle Auswahllisten in einer Antwort. Die Oberflaeche baut keine davon
  // nach: sie kaeme sonst mit der naechsten Aenderung aus dem Tritt.
  auswahl: () => ruf<Auswahllisten>('/auswahl'),

  // Der Posteingang. Die Dateien gehen als Mehrteil-Sendung hinaus, deshalb
  // nicht ueber `ruf`: dort steht ein fester Inhaltstyp.
  eingang: (zustand = 'offen') => ruf<any>('/eingang?zustand=' + zustand),
  eingangAllesNeuLesen: () => ruf<any>('/eingang/alles-neu-lesen', { method: 'POST', body: '{}' }),
  eingangAllesNeuLesenStand: () => ruf<any>('/eingang/alles-neu-lesen'),
  eingangAnnehmen: (dateien: File[]) => sendeDateien<any>('/eingang', dateien),
  eingangEinsortieren: (id: string, auftragId?: string) =>
    ruf<any>(`/eingang/${id}/einsortieren`,
             { method: 'POST', body: JSON.stringify({ auftrag_id: auftragId ?? null }) }),
  eingangArt: (id: string, art: string) =>
    ruf<any>('/eingang/' + id, { method: 'PATCH', body: JSON.stringify({ art }) }),
  /** Die Art derer neu bestimmen, die der Server selbst nicht erkannt hat. */
  eingangNochmal: () =>
    ruf<any>('/eingang/nochmal', { method: 'POST', body: JSON.stringify({}) }),
  eingangVerwerfen: (id: string, grund: string) =>
    ruf<any>(`/eingang/${id}/verwerfen`,
             { method: 'POST', body: JSON.stringify({ grund }) }),
  // ------------------------------------------------- Einen Leser anlernen
  // GELERNT WIRD EINE REGEL UND KEIN WERT. Der Bildschirm schickt die
  // MARKIERUNG (Zeile, von, bis); woraus daraus ein Anker wird, rechnet der
  // Server. Eine zweite Fassung im Browser liefe beim ersten Nachtrag
  // auseinander, und dann speichert jemand eine Regel, die er nie gesehen
  // hat.
  /* ART UND ABSENDER GEHEN AN JEDEM WEG MIT.
     Marvin, 22.09.2026: „wenn er sie dann nicht zuordnen kann, dann kommt
     der leser, den ich öffnen kann." Eine Datei, die liegengeblieben ist,
     hat oft weder eine erkannte Art noch einen Mailabsender; beides setzt
     der Leser, und dann muss es an JEDEM der 3 Wege mitgehen - stünde es
     nur beim Holen des Textes, speicherte der letzte Weg die Regel unter
     der alten Art. */
  leseregelEingang: (eingangId: string, art = '', absender = '') => {
    const q = new URLSearchParams();
    if (art) q.set('art', art);
    if (absender) q.set('absender', absender);
    return ruf<any>('/leseregel/eingang/' + eingangId
      + (q.toString() ? '?' + q : ''));
  },
  leseregelProbe: (b: { eingang_id: string; feld: string; zeile: number;
                        von: number; bis: number;
                        art?: string; absender?: string }) =>
    ruf<any>('/leseregel/probe', { method: 'POST', body: JSON.stringify(b) }),
  leseregelSpeichern: (b: { eingang_id: string; feld: string; zeile: number;
                            von: number; bis: number;
                            art?: string; absender?: string }) =>
    ruf<any>('/leseregel', { method: 'POST', body: JSON.stringify(b) }),
  leseregeln: (absender = '', art = '') =>
    ruf<any>('/leseregel'
             + (absender ? '?absender=' + encodeURIComponent(absender) : '')
             + (art ? (absender ? '&' : '?') + 'art=' + encodeURIComponent(art) : '')),
  /* BEREICHE AUF DEM SCAN (.271) */
  lesebereiche: (eingangId: string, art = '', vordruck = '') => {
    const q = new URLSearchParams();
    if (art) q.set('art', art);
    if (vordruck) q.set('vordruck', vordruck);
    return ruf<any>('/leseregel/eingang/' + eingangId + '/bereiche' + (q.toString() ? '?' + q : ''));
  },
  lesebereichProbe: (w: any) =>
    ruf<{ text: string; wert: any; grund: string }>('/leseregel/bereich/probe', { method: 'POST', body: JSON.stringify(w) }),
  lesebereichSpeichern: (w: any) =>
    ruf<{ ok: boolean; klartext: string }>('/leseregel/bereich', { method: 'POST', body: JSON.stringify(w) }),
  lesebereichWeg: (id: string) =>
    ruf<{ ok: boolean }>('/leseregel/bereich/' + id, { method: 'DELETE' }),
  leseregelNeuLesen: (eingangId: string) =>
    ruf<{ ok: boolean; zeichen: number; ocr: any; auftrag_id: string | null }>('/leseregel/eingang/' + eingangId + '/neu-lesen', { method: 'POST', body: '{}' }),
  leseregelWeg: (id: string) =>
    ruf<any>('/leseregel/' + id, { method: 'DELETE' }),
  // Den Hintergrundlauf frueher anstossen. Er laeuft ohnehin alle fuenf
  // Minuten von selbst.
  // --------------------------------------------------------- Das Postfach
  // Ein- und ausgehende Mail in einer Liste (Migration 041). Der Zusteller
  // bleibt n8n; was hier steht, ist das Urteil darueber.
  postfach: (zustand = 'ohne_auftrag', suche = '') =>
    ruf<any>('/postfach?zustand=' + encodeURIComponent(zustand)
             + (suche ? '&suche=' + encodeURIComponent(suche) : '')),
  postfachMail: (id: string) => ruf<any>('/postfach/' + id),
  postfachZuordnen: (id: string, auftragId: string | null) =>
    ruf<any>('/postfach/' + id,
             { method: 'PATCH', body: JSON.stringify({ auftrag_id: auftragId }) }),
  postfachErledigt: (id: string, erledigt: boolean) =>
    ruf<any>('/postfach/' + id,
             { method: 'PATCH', body: JSON.stringify({ erledigt }) }),
  postfachAntwort: (id: string, b: { an?: string[]; cc?: string[]; betreff?: string; text: string }) =>
    ruf<any>('/postfach/' + id + '/antwort',
             { method: 'POST', body: JSON.stringify(b) }),
  postfachAmAuftrag: (auftragId: string) =>
    ruf<any>('/auftraege/' + auftragId + '/postfach'),
  /* NOTIZEN AN EINER MAIL, UND WER DARIN GENANNT IST (Migration 165).
     Die Liste der Nennbaren kommt vom SERVER: die Oberflaeche darf
     `/benutzer` gar nicht abrufen, und eine zweite Liste daneben liefe beim
     ersten neuen Zugang auseinander. */
  postfachNennbar: () => ruf<any>('/postfach/nennbar'),
  /* DAS POSTFACH ALS ARBEITSPLATZ (.285): Vorgänge statt Mails. */
  postfachVorgaenge: (p: { ansicht: string; tag?: string; konto?: string; ort?: string; suche?: string }) =>
    ruf<any>('/postfach/vorgaenge?' + new URLSearchParams(
      Object.entries(p).filter(([, v]) => v) as [string, string][]).toString()),
  postfachVorgang: (faden: string) =>
    ruf<any>('/postfach/vorgang?faden=' + encodeURIComponent(faden)),
  postfachVorgangAendern: (faden: string, b: Record<string, unknown>) =>
    ruf<any>('/postfach/vorgang', { method: 'PATCH', body: JSON.stringify({ faden, ...b }) }),
  postfachTags: () => ruf<any>('/postfach/tags'),
  postfachServer: (faden: string) =>
    ruf<any>('/postfach/vorgang/server?faden=' + encodeURIComponent(faden)),
  mailfachAnhangAdresse: (konto: string, ordner: string, uid: number, teil: string) =>
    '/api/mailfach/' + konto + '/anhang?ordner=' + encodeURIComponent(ordner) + '&uid=' + uid + '&teil=' + encodeURIComponent(teil),
  postfachTagFarbe: (name: string, farbe: string) =>
    ruf<any>('/postfach/tags', { method: 'PUT', body: JSON.stringify({ name, farbe }) }),
  postfachAdressen: (q: string) => ruf<any>('/postfach/adressen?q=' + encodeURIComponent(q)),
  postfachNeu: (b: { an: string[]; cc?: string[]; betreff: string; text: string; auftrag_id?: string | null; tags?: string[] }) =>
    ruf<any>('/postfach/neu', { method: 'POST', body: JSON.stringify(b) }),
  postfachNotiz: (id: string, text: string) =>
    ruf<any>('/postfach/' + id + '/notiz',
             { method: 'POST', body: JSON.stringify({ text }) }),
  postfachNotizAendern: (id: string, b: { text?: string; weg?: boolean }) =>
    ruf<any>('/postfach/notiz/' + id,
             { method: 'PATCH', body: JSON.stringify(b) }),

  /* ------------------------------------------------------------------ *
   * DAS MAILFACH: das Postfach SELBST, für die Hand.
   *
   * Der automatische Weg läuft weiter über n8n (siehe oben); diese Wege
   * zeigen den Mailserver und keine Abschrift davon. Was hier verschoben
   * oder gelöscht wird, ist im Postfach verschoben oder gelöscht.
   * ------------------------------------------------------------------ */
  mailkonten: () => ruf<any>('/mailfach/konten'),
  mailkontoAnlegen: (b: any) =>
    ruf<any>('/mailfach/konten', { method: 'POST', body: JSON.stringify(b) }),
  mailkontoAendern: (id: string, b: any) =>
    ruf<any>('/mailfach/konten/' + id,
             { method: 'PATCH', body: JSON.stringify(b) }),
  mailkontoWeg: (id: string) =>
    ruf<any>('/mailfach/konten/' + id, { method: 'DELETE' }),
  mailkontoPruefen: (id: string) =>
    ruf<any>('/mailfach/konten/' + id + '/pruefen',
             { method: 'POST', body: '{}' }),

  mailfachOrdner: (id: string) => ruf<any>('/mailfach/' + id + '/ordner'),
  mailfachMails: (id: string, ordner: string, seite = 0, suche = '') =>
    ruf<any>('/mailfach/' + id + '/mails?ordner=' + encodeURIComponent(ordner)
             + '&seite=' + seite
             + (suche ? '&suche=' + encodeURIComponent(suche) : '')),
  mailfachMail: (id: string, ordner: string, uid: number) =>
    ruf<any>('/mailfach/' + id + '/mail?ordner=' + encodeURIComponent(ordner)
             + '&uid=' + uid),
  /** Die Adresse eines Anhangs. Sie wird ANGEZEIGT und nicht geholt. */
  mailfachAnhangWeg: (id: string, ordner: string, uid: number, teil: string) =>
    '/api/mailfach/' + id + '/anhang?ordner=' + encodeURIComponent(ordner)
    + '&uid=' + uid + '&teil=' + encodeURIComponent(teil),
  mailfachVerschieben: (id: string, b: any) =>
    ruf<any>('/mailfach/' + id + '/verschieben',
             { method: 'POST', body: JSON.stringify(b) }),
  mailfachLoeschen: (id: string, b: any) =>
    ruf<any>('/mailfach/' + id + '/loeschen',
             { method: 'POST', body: JSON.stringify(b) }),
  mailfachAnAuftrag: (id: string, b: any) =>
    ruf<any>('/mailfach/' + id + '/an-auftrag',
             { method: 'POST', body: JSON.stringify(b) }),
  mailfachSenden: (id: string, b: any) =>
    ruf<any>('/mailfach/' + id + '/senden',
             { method: 'POST', body: JSON.stringify(b) }),
  mailfachVorlagen: () => ruf<any>('/mailfach/vorlagen'),

  eingangVerarbeiten: () =>
    ruf<any>('/eingang/verarbeiten', { method: 'POST', body: JSON.stringify({}) }),

  // Der Import. Er ist kein eigener Bildschirm mehr: die Abruf- und
  // BAFA-Dateien kommen durch dieselbe Tuer wie alles andere.
  /* Die einmalige Übernahme aus dem XLSX-Export der alten Tabelle. EINE
     Datei, dazu die Art: „tabelle_gewinnt" oder „nur_fuellen". */
  uebernahmeVorschau: (datei: File, art: string) =>
    sendeDateiMitFeldern<any>('/uebernahme/vorschau', datei, { art }),
  uebernahmeAnwenden: (datei: File, art: string) =>
    sendeDateiMitFeldern<any>('/uebernahme/anwenden', datei, { art }),
  uebernahmeLaeufe: () => ruf<any[]>('/uebernahme/laeufe'),
  /* DER REPORT: welche Aufträge neu sind, welche geändert (02.09.2026). Er
     hängt am LAUF, nicht an der Antwort des Anwendens - zwischen dem Einlesen
     der Tabelle und dem Abwerfen der Ordner liegt ein Bildschirmwechsel. */
  uebernahmeReport: (lauf: string) => ruf<any>(`/uebernahme/laeufe/${lauf}/report`),
  uebernahmeReportCsv: (lauf: string) => `/api/uebernahme/laeufe/${lauf}/report.csv`,
  /* Die Dateien aus der alten Ablage, als ganzer Ordner (02.09.2026). Erst
     die Pfade zur Vorschau, dann die Bytes in Paketen, jedes mit dem Lauf. */
  ordnerVorschau: (pfade: string[]) =>
    ruf<any>('/uebernahme/dateien/vorschau', { method: 'POST', body: JSON.stringify({ pfade }) }),
  ordnerBeginn: (dateien: number) =>
    ruf<{ lauf: string }>('/uebernahme/dateien/beginn', { method: 'POST', body: JSON.stringify({ dateien }) }),
  ordnerPaket: (lauf: string, stuecke: { datei: File; pfad: string }[]) =>
    sendeBaum<{ ergebnisse: any[] }>('/uebernahme/dateien/paket', stuecke, { lauf }),
  ordnerEnde: (lauf: string) =>
    ruf<any>('/uebernahme/dateien/ende', { method: 'POST', body: JSON.stringify({ lauf }) }),
  ordnerLaeufe: () => ruf<any[]>('/uebernahme/dateien/laeufe'),
  /* PAPIER UND DATEI ZUSAMMENBRINGEN (07.09.2026). Die Zahl ist billig und
     steht beim Öffnen da; was WIRKLICH zusammenkäme, sagt die Probe - und
     die geht denselben Weg wie das Anwenden und rollt ihn zurück. */
  papiereOhneDatei: () => ruf<{
    papiere: number; auftraege: number; dateien_ohne_papier: number;
    nach_art: { art: string; anzahl: number }[];
  }>('/papiere/ohne-datei'),
  papiereVerknuepfen: (anwenden: boolean) => ruf<{
    anwenden: boolean; auftraege: number; verknuepft: number;
    offen: { grund: string; anzahl: number }[]; klartext: string;
  }>('/papiere/verknuepfen',
     { method: 'POST', body: JSON.stringify({ anwenden }) }),
  /* DIE BAUAKTEN, DIE NIE ZU ENDE GELESEN WURDEN (08.09.2026). Derselbe Bau
     wie darüber: eine billige Zahl beim Öffnen, eine Probe, die denselben Weg
     geht (`lies` mit `nurAnsehen`), und erst dann der Lauf. */
  bauaktenUngelesen: () => ruf<{
    mit_akte: number; ungelesen: number; stumm: number;
  }>('/bauakten/ungelesen'),
  bauaktenLesen: (anwenden: boolean) => ruf<{
    anwenden: boolean; auftraege: number; gelesen: number; gefuellt: number;
    konflikte: number; offen: { grund: string; anzahl: number }[];
    klartext: string;
  }>('/bauakten/lesen',
     { method: 'POST', body: JSON.stringify({ anwenden }) }),
  /* DAS TRANSFERVERZEICHNIS (05.09.2026): die Ordner kommen über WinSCP in
     einen Briefkasten auf dem Server, angesehen und übertragen wird aus der
     Oberfläche - EIN Ordner je Aufruf, damit man sieht, wo es hakt. */
  transferLesen: () => ruf<any>('/transfer'),
  transferRest: (ordner: string) =>
    ruf<{ klartext: string; ordnerWeg: boolean }>('/transfer/rest', { method: 'POST', body: JSON.stringify({ ordner }) }),
  transferLauf: (ordner: string) =>
    ruf<any>('/transfer/lauf', { method: 'POST', body: JSON.stringify({ ordner }) }),
  transferZuordnen: (ordner: string, auftrag: string | null) =>
    ruf<any>('/transfer/zuordnen',
             { method: 'POST', body: JSON.stringify({ ordner, auftrag }) }),
  transferOffen: () => ruf<any>('/transfer/offen'),
  /* DER LAUF ÜBER ALLE ORDNER LIEGT IM SERVER (06.09.2026).
     Er lief bis .120 als Schleife IN DIESEM FENSTER: wer die Seite verliess,
     nahm ihn mit, und sein Stand stand in einem Browser, den niemand mehr
     sieht. Jetzt wird er angestossen, sein Stand abgefragt und angehalten -
     drei Wege, drei Fragen. */
  transferMassenlauf: () => ruf<any>('/transfer/massenlauf', { method: 'POST' }),
  transferMassenstand: () => ruf<any>('/transfer/massenlauf'),
  transferMassenAnhalten: () =>
    ruf<any>('/transfer/massenlauf/anhalten', { method: 'POST' }),
  /* Ein ZIP statt eines abgeworfenen Ordners (02.09.2026). Es geht EINMAL
     hoch; die Pakete danach nennen nur noch seine Prüfsumme. */
  ordnerArchiv: (datei: File) => sendeDateiMitFeldern<any>('/uebernahme/dateien/archiv', datei, {}),
  ordnerArchivPaket: (lauf: string, archiv: string, von: number) =>
    ruf<any>('/uebernahme/dateien/archiv/paket',
             { method: 'POST', body: JSON.stringify({ lauf, archiv, von }) }),
  ordnerArchivWeg: (archiv: string) =>
    ruf<any>('/uebernahme/dateien/archiv/weg', { method: 'POST', body: JSON.stringify({ archiv }) }),
  importVorschau: (dateien: File[]) => sendeDateien('/import/vorschau', dateien),
  importAnwenden: (dateien: File[]) => sendeDateien('/import/anwenden', dateien),
  // Derselbe Import, aber aus dem Posteingang statt vom Schreibtisch.
  importVorschauAusEingang: (ids: string[]) =>
    ruf<any>('/import/vorschau', { method: 'POST', body: JSON.stringify({ eingang_ids: ids }) }),
  importAnwendenAusEingang: (ids: string[]) =>
    ruf<any>('/import/anwenden', { method: 'POST', body: JSON.stringify({ eingang_ids: ids }) }),
  // Auftrag vorbereiten und die Planung des Tiefbauaufwands. Alles, was der
  // Bildschirm braucht, kommt in EINER Antwort: früher lief je Klick ein
  // Aufruf, und Marvin am 30.07.2026: „weil es läd sehr langsam die anzeige."
  vorbereiten: (id: string) => ruf<any>(`/auftraege/${id}/vorbereiten`),
  planungskarte: (id: string) => ruf<any>(`/auftraege/${id}/planungskarte`),
  vorbereitenSpeichern: (id: string, werte: Record<string, string>) =>
    ruf<any>(`/auftraege/${id}/vorbereiten`,
             { method: 'POST', body: JSON.stringify({ werte }) }),
  tiefbau: (id: string) => ruf<any>(`/auftraege/${id}/tiefbau`),
  tiefbauSpeichern: (id: string, z: any) =>
    ruf<any>(`/auftraege/${id}/tiefbau`, { method: 'POST', body: JSON.stringify(z) }),

  // Die Rotberichtigung. Die Vorschau BAUT NICHTS: sie sagt, was in der Mappe
  // stünde und was fehlt. Erst `rbBauen` erzeugt wirklich eine Datei.
  // DIE AUSWAHL VON HAND GEHT MIT - AUCH IN DIE VORSCHAU.
  //
  // ORIGINALTEXT MARVIN (22.09.2026)
  //   „wenn die RB manuell erstellt wird muss ich die möglichkeiten haben,
  //    die ordner/dateien selbst noch zu selektieren, falls das system was
  //    nicht richtig erkannt hat."
  //
  // Sie KÖNNTE im Browser bleiben und dort mitgezählt werden. Dann stünde die
  // Regel zweimal da, und beim ersten Nachtrag zeigte der Bildschirm eine
  // andere Zahl als die Mappe hinterher trägt. Der Bildschirm fragt, der
  // Server rechnet - dieselbe Bauart wie bei den Nachweisen.
  rb: (id: string, art?: string, aus?: RbAuswahl) => {
    const p = new URLSearchParams();
    if (art) p.set('art', art);
    if (aus?.ohne?.length) p.set('ohne', aus.ohne.join(','));
    if (aus?.dazu?.length) {
      p.set('dazu', aus.dazu.map((d) => `${d.bezugId}:${d.kapitel}`).join(','));
    }
    const s = p.toString();
    return ruf<any>(`/auftraege/${id}/rotberichtigung${s ? '?' + s : ''}`);
  },
  rbArt: (id: string, art: string | null) =>
    ruf<any>(`/auftraege/${id}/rotberichtigung/art`,
             { method: 'POST', body: JSON.stringify({ art }) }),
  rbBauen: (id: string, art: string, auswahl?: RbAuswahl) =>
    ruf<any>(`/auftraege/${id}/rotberichtigung`,
             { method: 'POST',
               body: JSON.stringify({ art, auswahl: auswahl ?? null }) }),
  // DIE NACHWEISE VON HAND, OHNE DIE GANZE MAPPE.
  //
  // ORIGINALTEXT MARVIN (14.09.2026)
  //   „Nachweis von Hand erzeugen"
  //
  // Ohne `oz` entstehen alle, mit `oz` genau der eine. Es ist DERSELBE Bau
  // wie in `rbBauen` - der Weg dahinter ruft dieselbe Funktion -, und
  // deshalb heftet er dieselben Bilder in derselben Reihenfolge. Zwei
  // Fassungen liefen beim ersten Nachtrag auseinander, und dann trägt der
  // Nachweis aus der Mappe andere Seiten als der von Hand erzeugte.
  rbNachweise: (id: string, oz?: string | null) =>
    ruf<any>(`/auftraege/${id}/rotberichtigung/nachweise`,
             { method: 'POST', body: JSON.stringify({ oz: oz ?? null }) }),

  // Die Liste über ALLE Aufträge: welche Abgabe ist fällig, welche steht
  // schon. Sie rechnet mit derselben Abfrage wie der nächtliche Lauf.
  rbListe: (zeigen = 'faellig') =>
    ruf<any>('/rotberichtigungen?zeigen=' + encodeURIComponent(zeigen)),
  // Zurückziehen ist kein Löschen: die Datei bleibt, der Grund kommt dazu.
  rbZurueckziehen: (id: string, grund: string) =>
    ruf<any>('/rotberichtigungen/' + id + '/zurueckziehen',
             { method: 'POST', body: JSON.stringify({ grund }) }),

  // Der Kartenlageplan. Die Kacheln laufen über den EIGENEN Server: nur so
  // lässt sich das Zeichenfeld im Browser auslesen, und daran hängt die
  // Oberflächenerkennung im Tiefbau.
  lageplan: (id: string) => ruf<any>(`/auftraege/${id}/lageplan`),
  lageplanSpeichern: (id: string, w: any) =>
    ruf<any>(`/auftraege/${id}/lageplan`,
             { method: 'POST', body: JSON.stringify(w) }),
  lapMaps: (id: string, w: any) =>
    ruf<any>(`/auftraege/${id}/lageplan/erzeugen`,
             { method: 'POST', body: JSON.stringify(w) }),
  flurstuecke: (id: string, k: { w: number; s: number; o: number; n: number }) =>
    ruf<any>(`/auftraege/${id}/flurstuecke`
      + `?w=${k.w}&s=${k.s}&o=${k.o}&n=${k.n}`),
  // Die Umgebungsbilder. Die Vorschau legt NICHTS ab: erst ansehen, dann
  // ablegen. Der Grund steht früher, ein Blumenladen von innen.
  // `grade` holt DIESELBEN Ansichten unter einem anderen Winkel. Ohne sie
  // rechnet der Server die vier Blickrichtungen wie bisher aus.
  umgebung: (id: string, markierungen?: any, grade?: number[]) => {
    const p = new URLSearchParams();
    if (markierungen) p.set('markierungen', JSON.stringify(markierungen));
    if (grade?.length) p.set('grade', grade.join(','));
    const q = p.toString();
    return ruf<any>(`/auftraege/${id}/umgebung` + (q ? '?' + q : ''));
  },
  umgebungAblegen: (id: string, w: any) =>
    ruf<any>(`/auftraege/${id}/umgebung`,
             { method: 'POST', body: JSON.stringify(w) }),

  geoPruefen: (plz?: string) =>
    ruf<any>('/geo/pruefen' + (plz ? '?plz=' + encodeURIComponent(plz) : '')),
  geoNeuLesen: () => ruf<any>('/geo/neu-lesen', { method: 'POST' }),

  // Die Automatik. Was von selbst läuft, und was dabei herauskam.
  automatik: () => ruf<any>('/automatik'),
  automatikJetzt: (s: string) =>
    ruf<any>(`/automatik/${s}/jetzt`, { method: 'POST' }),
  automatikAktiv: (s: string, aktiv: boolean) =>
    ruf<any>(`/automatik/${s}/aktiv`,
             { method: 'POST', body: JSON.stringify({ aktiv }) }),

  // Wohin welcher Anlass meldet, und der Rückweg von Telegram.
  //
  // ES SIND ZWEI VERSCHIEDENE FRAGEN, und deshalb zwei Aufrufe: „in welche
  // Gruppe geht diese Meldung" pflegt man laufend, „zeigt der Webhook
  // hierher" fragt man einmal beim Einrichten. Der zweite geht dabei WIRKLICH
  // zu Telegram und dauert; ihn in den ersten zu packen hiesse, dass jeder
  // Aufruf dieser Seite auf einen fremden Server wartet.
  telegramAnlaesse: () => ruf<any>('/telegram/anlaesse'),
  /* EINE KENNUNG AN MEHRERE ANLAESSE. Der Weg, den man wirklich geht: ein
     Thema in einer Gruppe nimmt selten genau einen Anlass auf. */
  telegramAnlaesseSetzen: (chat_id: string, anlaesse: string[]) =>
    ruf<{ ok: boolean; geaendert: number }>('/telegram/anlaesse',
      { method: 'PUT', body: JSON.stringify({ chat_id, anlaesse }) }),
  telegramAnlass: (anlass: string, chat_id: string, aktiv?: boolean,
                   per_mail?: boolean, an_personen?: boolean) =>
    ruf<any>(`/telegram/anlaesse/${encodeURIComponent(anlass)}`,
             { method: 'PUT',
               body: JSON.stringify({ chat_id, aktiv, per_mail, an_personen }) }),
  telegramWebhook: () => ruf<any>('/telegram/webhook'),
  telegramWebhookSetzen: (uebernehmen = false) =>
    ruf<any>('/telegram/webhook',
             { method: 'POST', body: JSON.stringify({ uebernehmen }) }),

  // --- Der Betrieb: Einblasstop, Verfügbarkeit, Poller, Rechnungen, Post ---
  //
  // Fünf Tabellen, die es seit Migration 001 gibt und für die es bis zum
  // 24.08.2026 keine einzige Zeile Programmcode gab.

  // Freigeben und Abschliessen. Früher waren das vier Menüpunkte; die
  // Zustandsfolge ist für Aufmaß und Montage dieselbe.
  doku: (id: string) => ruf<any>(`/auftraege/${id}/doku`),
  dokuSchritt: (id: string, was: string, schritt: string,
                grund: string | null, telegram = true) =>
    ruf<any>(`/auftraege/${id}/doku/${was}/${schritt}`,
             { method: 'POST', body: JSON.stringify({ grund, telegram }) }),

  // Der WFMT-Verlauf aus dem Vorsystem der Telekom.
  wfmt: (id: string) => ruf<any>(`/auftraege/${id}/wfmt`),

  // Ort → Gemeinde. Erst der Bericht, dann das Schreiben.
  ortszuordnung: () => ruf<any>('/ortszuordnung'),
  ortszuordnungOffen: () => ruf<any[]>('/ortszuordnung/offen'),
  ortszuordnungAnwenden: (korrigieren: boolean) =>
    ruf<any>('/ortszuordnung/anwenden',
             { method: 'POST', body: JSON.stringify({ korrigieren }) }),
  ortSetzen: (w: any) =>
    ruf<any>('/ortszuordnung/ort', { method: 'POST', body: JSON.stringify(w) }),
  /* WAS EINE SPERRE KOSTET, BEVOR SIE GILT. Seit dem 08.09.2026 archiviert
     sie auch Aufträge, an denen Arbeit hängt - das wird nicht blind
     gemacht. */
  ortSperrprobe: (ort: string) =>
    ruf<any>(`/ortszuordnung/sperrprobe?ort=${encodeURIComponent(ort)}`),

  // Regelpläne. Gelöscht wird nicht, sondern ausser Kraft gesetzt.
  regelplaene: () => ruf<any[]>('/regelplaene'),
  regelplanSetzen: (w: any) =>
    ruf<any>('/regelplaene', { method: 'POST', body: JSON.stringify(w) }),
  /* DIE PDF ZUM REGELPLAN (.242). Ohne `id` ordnet der Server jede Datei
     über ihren Namen zu. */
  async regelplanDateien(dateien: File[], id?: string) {
    const f = new FormData();
    if (id) f.append('id', id);
    for (const d of dateien) f.append('datei', d, d.name);
    const a = await fetch('/api/regelplaene/dateien',
                          { method: 'POST', credentials: 'same-origin', body: f });
    const text = await a.text();
    const daten = text ? JSON.parse(text) : null;
    if (!a.ok) throw Object.assign(new Error(daten?.fehler ?? a.statusText), { daten });
    melde('/regelplaene', { method: 'POST' });
    return daten as { ok: boolean; zugeordnet: { datei: string; regelplan: string }[]; ohne: string[] };
  },
  regelplanDateiAdresse: (id: string) => '/api/regelplaene/' + id + '/datei',

  /* MATERIAL (.249). Bestand, Katalog, Bestellungen, Bewegungen, Übernahme. */
  materialBestand: () => ruf<any[]>('/material/bestand'),
  materialArtikel: () => ruf<any[]>('/material/artikel'),
  materialVpeEtikett: (id: string, aktion: 'uebernehmen' | 'passt') =>
    ruf<any>('/material/artikel/' + id + '/vpe-etikett', { method: 'POST', body: JSON.stringify({ aktion }) }),
  materialArtikelEins: (id: string) => ruf<any>('/material/artikel/' + id),
  materialArtikelNeu: (w: any) =>
    ruf<{ id: string; mat_nr: string }>('/material/artikel', { method: 'POST', body: JSON.stringify(w) }),
  materialArtikelAendern: (id: string, w: any) =>
    ruf<{ ok: boolean }>('/material/artikel/' + id, { method: 'PATCH', body: JSON.stringify(w) }),
  materialBilderHoch: (id: string, dateien: File[]) =>
    sendeDateien<{ ok: boolean; bilder: { datei: string; neu: boolean }[] }>(
      '/material/artikel/' + id + '/bilder', dateien),
  materialBildWeg: (id: string) =>
    ruf<{ ok: boolean }>('/material/bilder/' + id, { method: 'DELETE' }),
  materialBildAdresse: (id: string) => '/api/material/bilder/' + id,
  materialBestellungen: () => ruf<{ bestellungen: any[]; einstellungen: Record<string, string | null> }>('/material/bestellungen'),
  materialBestellung: (id: string) => ruf<any>('/material/bestellungen/' + id),
  materialBestellungPdfAdresse: (id: string) => '/api/material/bestellungen/' + id + '/pdf',
  materialWareneingang: () => ruf<any[]>('/material/wareneingang'),
  materialEntnahmen: () => ruf<{ entnahmen: any[]; firmen: any[]; eigene_firma: string | null }>('/material/entnahmen'),
  materialInventur: () => ruf<any[]>('/material/inventur'),
  materialUebernahmen: () => ruf<{ laeufe: any[]; stand: any }>('/material/uebernahme'),
  materialUebernehmen: (datei: File) => sendeDateien<any>('/material/uebernahme', [datei]),
  /* Buchen und bestellen (.250). */
  materialAuswahl: () => ruf<any[]>('/material/artikel/auswahl'),
  materialWerte: () => ruf<Record<'kategorie' | 'einheit' | 'bme' | 'vpe', string[]>>('/material/artikel/werte'),
  materialEntnahmeBuchen: (w: any) => ruf<any>('/material/entnahmen', { method: 'POST', body: JSON.stringify(w) }),
  materialEntnahmeStorno: (id: string, grund: string) =>
    ruf<any>('/material/entnahmen/' + id + '/storno', { method: 'POST', body: JSON.stringify({ grund }) }),
  materialEntnahmeFotoAdresse: (id: string) => '/api/material/entnahmen/' + id + '/foto',
  materialLieferscheinLesen: (datei: File) => sendeDateien<any>('/material/wareneingang/lesen', [datei]),
  materialWareneingangBuchen: (w: any) => ruf<any>('/material/wareneingang', { method: 'POST', body: JSON.stringify(w) }),
  materialWareneingangStorno: (nummer: string, grund: string) =>
    ruf<any>('/material/wareneingang/' + encodeURIComponent(nummer) + '/storno', { method: 'POST', body: JSON.stringify({ grund }) }),
  materialZaehlliste: () => ruf<any[]>('/material/inventur/liste'),
  materialInventurBuchen: (zeilen: any[]) => ruf<any>('/material/inventur', { method: 'POST', body: JSON.stringify({ zeilen }) }),
  materialBestellungNeu: (vorschlag: string) =>
    ruf<{ id: string; nummer: number; neu: boolean }>('/material/bestellungen', { method: 'POST', body: JSON.stringify({ vorschlag }) }),
  materialBestellungAendern: (id: string, w: any) =>
    ruf<any>('/material/bestellungen/' + id, { method: 'PATCH', body: JSON.stringify(w) }),
  materialBestellungVerwerfen: (id: string) => ruf<any>('/material/bestellungen/' + id, { method: 'DELETE' }),
  materialPositionNeu: (id: string, w: any) =>
    ruf<any>('/material/bestellungen/' + id + '/positionen', { method: 'POST', body: JSON.stringify(w) }),
  materialPositionAendern: (id: string, w: any) =>
    ruf<any>('/material/bestellpositionen/' + id, { method: 'PATCH', body: JSON.stringify(w) }),
  materialPositionWeg: (id: string) => ruf<any>('/material/bestellpositionen/' + id, { method: 'DELETE' }),
  materialBestellungVorschauAdresse: (id: string) => '/api/material/bestellungen/' + id + '/vorschau',
  materialVorschlaege: () => ruf<any[]>('/material/vorschlaege'),
  materialInDenWarenkorb: (positionen: { artikel_id: string; menge?: any; einheit?: string | null; bemerkung?: string; trommeln?: string }[]) =>
    ruf<{ bestellung_id: string; nummer: number; positionen: number }>('/material/warenkorb', { method: 'POST', body: JSON.stringify({ positionen }) }),
  materialLiefertermin: (id: string, tag: string | null) =>
    ruf<{ ok: boolean; positionen: number }>('/material/bestellungen/' + id + '/liefertermin', { method: 'POST', body: JSON.stringify({ tag }) }),
  materialBestellungSenden: (id: string) =>
    ruf<any>('/material/bestellungen/' + id + '/versenden', { method: 'POST', body: '{}' }),
  materialOffenXlsxAdresse: () => '/api/material/offen.xlsx',

  // Die Glasfaser-Terminliste an die Telekom.
  glasfaser: (jahr?: number, kw?: number) =>
    ruf<any>('/glasfaser/termine'
      + (jahr && kw ? `?jahr=${jahr}&kw=${kw}` : '')),
  glasfaserSenden: (w: any) =>
    ruf<any>('/glasfaser/senden', { method: 'POST', body: JSON.stringify(w) }),

  // Das Handbuch. Es steht in der Datenbank: eine Anleitung, die man nur mit
  // einer Lieferung ändern kann, veraltet zwischen zwei Lieferungen.
  handbuch: (modul?: string) =>
    ruf<any[]>('/handbuch?bereich=' + bereichJetzt() + (modul ? '&modul=' + encodeURIComponent(modul) : '')),
  handbuchSetzen: (w: any) =>
    ruf<any>('/handbuch', { method: 'POST', body: JSON.stringify(w) }),

  // Die Bauakte. Der GET sieht nur nach, der POST übernimmt: erst ansehen,
  // dann schreiben.
  bauakte: (id: string) => ruf<any>(`/auftraege/${id}/bauakte`),
  bauakteLesen: (id: string) =>
    ruf<any>(`/auftraege/${id}/bauakte`, { method: 'POST' }),
  bauakteKonflikte: (id: string) =>
    ruf<any[]>(`/auftraege/${id}/bauakte/konflikte`),
  bauakteKonflikt: (id: string, kid: string, uebernehmen: boolean) =>
    ruf<any>(`/auftraege/${id}/bauakte/konflikte/${kid}`,
             { method: 'POST', body: JSON.stringify({ uebernehmen }) }),

  // „Fertig melden". Der Weg sagt zuerst, WAS fehlt, und hakt erst dann ab.
  /* `kabelImHaus` FRAGT „WAS WÄRE, WENN" und schreibt nichts. Der Haken im
     Fertig-Dialog muss die Ordnerliste sofort ändern; gerechnet wird die
     Freistellung auf dem Server, wo die Regel steht. */
  /* EIN OPTIONSOBJEKT UND KEINE REIHE VON STELLEN.
     Hier stand `(id, kabelImHaus = false)`. Mit dem zweiten Haken („kein
     Tiefbau nötig") wäre daraus `(id, kabel, keinTiefbau)` geworden, mit dem
     dritten `(id, kabel, keinTiefbau, druckluft)` - und spätestens dann ruft
     jemand `api.fertigmeldung(id, false, true)` und meint etwas anderes.
     **Eine Reihe von Wahrheitswerten an einer Aufrufstelle ist eine Zeile,
     die man nicht lesen kann.** */
  fertigmeldung: (id: string, alsOb: {
    kabelImHaus?: boolean; keinTiefbau?: boolean;
  } = {}) => {
    const p = new URLSearchParams();
    if (alsOb.kabelImHaus) p.set('kabel_im_haus', '1');
    if (alsOb.keinTiefbau) p.set('kein_tiefbau', '1');
    const q = p.toString();
    return ruf<any>(`/auftraege/${id}/fertigmeldung` + (q ? '?' + q : ''));
  },
  fertigMelden: (id: string, w: any) =>
    ruf<any>(`/auftraege/${id}/fertigmeldung`,
             { method: 'POST', body: JSON.stringify(w) }),

  einblasstopListe: () => ruf<any[]>('/einblasstop'),
  einblasstop: (id: string) => ruf<any>(`/auftraege/${id}/einblasstop`),
  partnerfreigaben: (id: string) => ruf<any>(`/auftraege/${id}/freigaben`),
  partnerfreigeben: (id: string, w: { subunternehmer_id: string; grund: string }) =>
    ruf<any>(`/auftraege/${id}/freigaben`, { method: 'POST', body: JSON.stringify(w) }),
  partnerentziehen: (id: string, fid: string) =>
    ruf<any>(`/auftraege/${id}/freigaben/${fid}`, { method: 'DELETE' }),
  einblasstopTelekom: (id: string) => ruf<any>(`/auftraege/${id}/einblasstop/telekom`),
  einblasstopTelekomSenden: (id: string, w: { an: string; betreff: string; text: string }) =>
    ruf<any>(`/auftraege/${id}/einblasstop/telekom`, { method: 'POST', body: JSON.stringify(w) }),
  /* Schon außerhalb gemeldet: abhaken (.328). */
  einblasstopSchonGemeldet: (id: string) =>
    ruf<any>(`/auftraege/${id}/einblasstop/telekom/schon-gemeldet`, { method: 'POST', body: '{}' }),
  einblasstopMelden: (id: string, w: any) =>
    ruf<any>(`/auftraege/${id}/einblasstop`,
             { method: 'POST', body: JSON.stringify(w) }),
  // Der Grund steht in der Adresse, nicht im Rumpf: ein DELETE mit Rumpf
  // kommt nicht überall an.
  /* EINE RICHTUNG ODER ALLE. Ohne `seite` gehen alle Meldungen zurück - das
     ist der Fall „es geht doch weiter". Mit `seite` genau eine, und die
     andere bleibt stehen. */
  einblasstopZurueck: (id: string, grund: string, seite?: string) =>
    ruf<any>(`/auftraege/${id}/einblasstop?grund=` + encodeURIComponent(grund)
             + (seite ? '&seite=' + encodeURIComponent(seite) : ''),
             { method: 'DELETE' }),

  /* WAS VON DER BAUSTELLE ZURÜCKKOMMT (Migration 188).
     ------------------------------------------------------------------------
     ORIGINALTEXT MARVIN (08.09.2026)
       „hier geht es um die rückmeldungen die der tiefbau draußen macht ...
        diese infos sollen dann ja für alle anderen sichtbar sein"

     Die vier Angaben der Pipe stammen aus der Planung und weichen draussen
     ab. Der Weg schreibt die ISTWERTE; der Planwert bleibt stehen, weil der
     Bauaktenlauf ihn bei jeder neuen Akte neu schreibt. */
  /* DIE QUITTUNG „AUFTRAG ABGERECHNET" (18.09.2026).
     Derselbe Weg setzt und nimmt zurueck: ein Zustand, den man nur in eine
     Richtung aendern kann, ist eine Falle. */
  abgerechnetQuittieren: (id: string, quittiert: boolean) =>
    ruf<any>(`/auftraege/${id}/abgerechnet`,
             { method: 'POST', body: JSON.stringify({ quittiert }) }),
  technikmeldung: (id: string) =>
    ruf<any>(`/auftraege/${id}/technikmeldung`),
  technikMelden: (id: string, w: any) =>
    ruf<any>(`/auftraege/${id}/technikmeldung`,
             { method: 'POST', body: JSON.stringify(w) }),

  /* „AUFTRAG NICHT ERLEDIGT". Häkchen statt Fliesstext, damit der
     Innendienst weiss, was er damit machen soll. */
  nichtErledigt: (id: string) => ruf<any>(`/auftraege/${id}/nicht-erledigt`),
  nichtErledigtMelden: (id: string, w: any) =>
    ruf<any>(`/auftraege/${id}/nicht-erledigt`,
             { method: 'POST', body: JSON.stringify(w) }),
  // Der Grund steht in der Adresse, nicht im Rumpf: ein DELETE mit Rumpf
  // kommt nicht überall an - dieselbe Zeile wie beim Einblasstop.
  nichtErledigtZurueck: (id: string, meldung: string, grund: string) =>
    ruf<any>(`/auftraege/${id}/nicht-erledigt/${meldung}?grund=`
             + encodeURIComponent(grund), { method: 'DELETE' }),

  /* „DIE KOLONNE IST ABGEFAHREN, DIE UNTERLAGEN KOMMEN NACH."
     ORIGINALTEXT MARVIN (15.09.2026)
       „die partner wünschen sich noch eine funktion, wo sie den auftrag per
        knopf fertig melden können, was wir im innendienst sehen, aber sie das
        aufmaß und die unterlagen später hochladen."
     SIE ERSETZT „FERTIG MELDEN" NICHT: dort wird das Gewerk abgehakt, und
     daran hängen Rotberichtigung, Abrechnung und Kapazität. Das Gewerk leitet
     der SERVER aus dem Anmelder ab - es geht hier bewusst nichts mit. */
  baustelleFertig: (id: string) =>
    ruf<any>(`/auftraege/${id}/baustelle-fertig`),
  baustelleFertigMelden: (id: string, w: any) =>
    ruf<any>(`/auftraege/${id}/baustelle-fertig`,
             { method: 'POST', body: JSON.stringify(w) }),
  // Der Grund steht in der Adresse, nicht im Rumpf - dieselbe Zeile wie oben.
  baustelleFertigZurueck: (id: string, meldung: string, grund: string) =>
    ruf<any>(`/auftraege/${id}/baustelle-fertig/${meldung}?grund=`
             + encodeURIComponent(grund), { method: 'DELETE' }),

  verfuegbarkeit: (von: string, bis: string, sub?: string) =>
    ruf<any>('/verfuegbarkeit?von=' + von + '&bis=' + bis
             + (sub ? '&sub=' + sub : '')),
  verfuegbarkeitSetzen: (w: any) =>
    ruf<any>('/verfuegbarkeit', { method: 'POST', body: JSON.stringify(w) }),
  /**
   * Mehrere Tage mit VERSCHIEDENEN Ständen auf einmal.
   *
   * Der Weg auf dem Server nimmt EINEN Zustand für eine Liste von Tagen - das
   * ist richtig so, denn genau das ist der Zeitraum-Fall. Der Kalender sammelt
   * aber Änderungen aus mehreren Antippern, und darin stehen verfügbar,
   * Urlaub und „keine Angabe" nebeneinander. Also wird gebündelt: höchstens
   * drei Aufrufe statt einer je Tag.
   */
  verfuegbarkeitMehrere: async (
    aenderungen: { datum: string; stand: 'frei' | 'urlaub' | 'weg' }[]
  ) => {
    const je: Record<string, string[]> = {};
    for (const a of aenderungen) (je[a.stand] ??= []).push(a.datum);
    for (const [stand, tage] of Object.entries(je)) {
      if (!tage.length) continue;
      await ruf<any>('/verfuegbarkeit', {
        method: 'POST',
        // „weg" heisst: die Zeile loeschen, also der Zustand, den der Server
        // „frei" nennt. „frei" im Kalender heisst „verfuegbar gemeldet".
        body: JSON.stringify({
          tage,
          zustand: stand === 'urlaub' ? 'urlaub'
                 : stand === 'weg' ? 'frei' : 'verfuegbar',
        }),
      });
    }
    return { ok: true };
  },

  poller: () => ruf<any>('/poller'),
  pollerFuerAuftrag: (id: string) => ruf<any>(`/auftraege/${id}/poller`),
  pollerCode: (w: any) =>
    ruf<any>('/poller/code', { method: 'POST', body: JSON.stringify(w) }),
  pollerGebiet: (name: string) =>
    ruf<any>('/poller/gebiet', { method: 'POST', body: JSON.stringify({ name }) }),
  pollerStrasse: (w: any) =>
    ruf<any>('/poller/strasse', { method: 'POST', body: JSON.stringify(w) }),

  rechnungen: (zustand = 'leb', suche?: string) =>
    ruf<any>('/rechnungen?zustand=' + encodeURIComponent(zustand)
      + (suche && suche.trim() ? '&suche=' + encodeURIComponent(suche.trim()) : '')),

  /* WAS DIE TELEKOM UNS ZAHLT, gegen das, was wir eingereicht haben.
     Marvin, 22.09.2026: „ich will das wir die gegen das leb setzen und
     markieren das leb, das es abgerechnet wurde." */
  /* Die Gutschriften nach Eingang (23.09.2026): „wo sehe ich zb die
     gutschriften die heute nacht gekommen sind?" */
  /** Die Datei einer Gutschrift, hinter „Zahlen sehen"; der Name hinten nur für den Browser. */
  /* FASSUNGEN EINER RECHNUNG (.408). */
  rechnungFassungAdresse: (id: string, fid: string) => '/api/rechnungen/' + id + '/fassung/' + fid + '/datei',
  rechnungKorrekturAdresse: (id: string, kid: string) => '/api/rechnungen/' + id + '/korrektur/' + kid + '/datei',
  rechnungFassungUebernehmen: (id: string, fid: string) =>
    ruf<{ ok: boolean; positionen: number }>('/rechnungen/' + id + '/fassung/' + fid + '/uebernehmen', { method: 'POST', body: '{}' }),
  gutschriftAdresse: (id: string, dateiname?: string | null) =>
    '/api/lebabrechnung/gutschriften/' + id + '/datei'
    + (dateiname ? '/' + encodeURIComponent(dateiname) : ''),
  neueGutschriften: (tage = 1) =>
    ruf<any>('/lebabrechnung/gutschriften?tage=' + tage),
  /** Prüfung abgeschlossen setzen oder zurücknehmen (.309). */
  gutschriftGeprueft: (id: string, offen = false, notiz: string | null = null) =>
    ruf<{ ok: boolean }>('/lebabrechnung/gutschriften/' + id + '/geprueft', { method: 'POST', body: JSON.stringify({ offen, notiz }) }),
  /** Eine (id) oder alle Gutschriften aus ihrer Datei neu lesen (.214). */
  gutschriftenNachlesen: (id?: string) =>
    ruf<any>('/lebabrechnung/gutschriften/nachlesen',
             { method: 'POST', body: JSON.stringify(id ? { id } : {}) }),
  lebAbrechnung: (p: { zustand?: string; monat?: string; beleg?: string; suche?: string }) => {
    const q = new URLSearchParams();
    if (p.suche && p.suche.trim()) q.set('suche', p.suche.trim());
    if (p.zustand) q.set('zustand', p.zustand);
    if (p.monat) q.set('monat', p.monat);
    if (p.beleg) q.set('beleg', p.beleg);
    return ruf<any>('/lebabrechnung' + (q.toString() ? '?' + q : ''));
  },
  /* DIE AUSFUHR GIBT DEN PFAD ZURÜCK UND HOLT SIE NICHT SELBST. Eine Datei,
     die der Browser herunterlädt, soll der BROWSER herunterladen: dann
     bekommt sie ihren Namen aus dem Kopf der Antwort, landet im
     Downloadordner und lässt sich anhängen. Wer sie hier in den Speicher
     holt, baut den Namen ein 2. Mal - und der 2. ist der falsche. */
  lebAusfuhrPfad: (p: { zustand?: string; monat?: string;
                        mitWerten?: boolean }) => {
    const q = new URLSearchParams();
    if (p.zustand) q.set('zustand', p.zustand);
    if (p.monat) q.set('monat', p.monat);
    if (p.mitWerten) q.set('mit_werten', 'ja');
    return '/api/lebabrechnung/ausfuhr' + (q.toString() ? '?' + q : '');
  },
  rechnungAnlegen: (w: any) =>
    ruf<any>('/rechnungen', { method: 'POST', body: JSON.stringify(w) }),
  /* DIE FREIGABE (.256): erst prüfen, dann in den Belege-Ordner. */
  rechnungFreigabe: () => ruf<{ karten: any[]; darf_freigeben: boolean; eingeschaltet: boolean }>('/rechnungen/freigabe'),
  rechnungFreigeben: (id: string, ausnahme?: string) =>
    ruf<{ ok: boolean; abgelegt: boolean; im_hintergrund?: boolean; fehler: string | null }>('/rechnungen/' + id + '/freigeben', { method: 'POST', body: JSON.stringify({ ausnahme: ausnahme ?? null }) }),
  rechnungenFreigeben: (ids: string[]) =>
    ruf<{ ok: boolean; freigegeben: number; abgelegt: number; uebersprungen: number; fehler: string[] }>('/rechnungen/freigeben', { method: 'POST', body: JSON.stringify({ ids }) }),
  rechnungAblehnen: (id: string, grund: string) =>
    ruf<{ ok: boolean; abgelegt: boolean; fehler: string | null }>('/rechnungen/' + id + '/ablehnen', { method: 'POST', body: JSON.stringify({ grund }) }),
  rechnungAblegen: (id: string) =>
    ruf<{ ok: boolean; fehler?: string }>('/rechnungen/' + id + '/ablegen', { method: 'POST', body: '{}' }),
  rechnungInDenPruefordner: (id: string) =>
    ruf<{ ok: boolean; pfad?: string; klartext?: string }>('/rechnungen/' + id + '/in-den-pruefordner', { method: 'POST', body: '{}' }),
  /* GEBÜHREN BEZAHLEN (.258) und die Anbindung an Revolut Business. */
  gebuehren: () => ruf<{ zeilen: any[]; summen: any; darf_bezahlen: boolean; revolut: any; erwartet?: any[] }>('/gebuehren'),
  gebuehrAendern: (id: string, w: any) =>
    ruf<{ ok: boolean }>('/gebuehren/' + id, { method: 'POST', body: JSON.stringify(w) }),
  gebuehrNachlesen: (id: string) =>
    ruf<{ ok: boolean }>('/gebuehren/' + id + '/nachlesen', { method: 'POST', body: '{}' }),
  gebuehrenBezahlt: (ids: string[], datum: string) =>
    ruf<{ ok: boolean; markiert: number }>('/gebuehren/bezahlt', { method: 'POST', body: JSON.stringify({ ids, datum }) }),
  gebuehrenGeprueft: (ids: string[]) =>
    ruf<{ ok: boolean; geprueft: number }>('/gebuehren/geprueft', { method: 'POST', body: JSON.stringify({ ids }) }),
  /** „Bescheid erwartet" abhaken, weil keiner kommt (.302). */
  /** Wo jeder Anhang einer Behördenmail gelandet ist (.309). */
  behoerdenpost: (seit?: string) => ruf<{ seit: string; zeilen: any[] }>('/gebuehren/behoerdenpost' + (seit ? '?seit=' + encodeURIComponent(seit) : '')),
  gebuehrErwartetErledigt: (id: string, grund?: string) =>
    ruf<{ ok: boolean }>('/gebuehren/erwartet/' + id + '/erledigt', { method: 'POST', body: JSON.stringify({ grund: grund ?? null }) }),
  gebuehrOffen: (id: string) =>
    ruf<{ ok: boolean }>('/gebuehren/' + id + '/offen', { method: 'POST', body: '{}' }),
  gebuehrenAnRevolut: (ids: string[]) =>
    ruf<{ entwurf_id: string; anzahl: number; summe: number; uebersprungen: string[] }>('/gebuehren/revolut', { method: 'POST', body: JSON.stringify({ ids }) }),
  gebuehrenKontoabgleich: () =>
    ruf<{ umsaetze: number; bezahlt: number; vorschlaege: number; gebuehren: number }>('/gebuehren/kontoabgleich', { method: 'POST', body: '{}' }),
  /** Den Revolut-Kontoauszug (CSV) einlesen und abgleichen (.304). */
  kontoauszugEinlesen: (csv: string) =>
    ruf<any>('/gebuehren/kontoauszug', { method: 'POST', body: JSON.stringify({ csv }) }),
  gebuehrenAbgleichen: () =>
    ruf<{ geprueft: number; bezahlt: number; zurueck: number }>('/gebuehren/abgleichen', { method: 'POST', body: '{}' }),
  gebuehrEmpfaenger: (id: string) =>
    ruf<{ ok: boolean }>('/gebuehren/' + id + '/empfaenger', { method: 'POST', body: '{}' }),
  /* DIE EMPFÄNGER UND DIE RECHNUNGEN DER PARTNER (.259). */
  zahlungsempfaenger: (art?: 'behoerde' | 'partner') =>
    ruf<{ empfaenger: any[] }>('/zahlungsempfaenger' + (art ? '?art=' + art : '')),
  zahlungsempfaengerEntfernen: (id: string) =>
    ruf<{ ok: boolean }>('/zahlungsempfaenger/' + id + '/entfernen', { method: 'POST', body: '{}' }),
  partnerSkontoNachholen: (ids: string[]) =>
    ruf<{ angelegt: number; summe: number }>('/partnerzahlung/skonto-nachholen', { method: 'POST', body: JSON.stringify({ ids }) }),
  partnerVerrechnungLoeschen: (id: string) =>
    ruf<{ ok: boolean }>('/partnerzahlung/verrechnung/' + encodeURIComponent(id) + '/loeschen', { method: 'POST', body: '{}' }),
  telekomAvise: () => ruf<{ avise: any[] }>('/telekom/avise'),
  telekomBeanstandung: (avisId: string, posten: string[]) => ruf<{ betreff: string; text: string; posten: number; summe: number; an: string; an_herkunft: string; konten: any[] }>('/telekom/avise/' + avisId + '/beanstandung', { method: 'POST', body: JSON.stringify({ posten }) }),
  /** .490: Adresse der .eml-Datei (wird mit dem Cookie geladen, kein Token in der Adresse). */
  telekomBeanstandungEml: (avisId: string, posten: string[], an: string) =>
    '/api/telekom/avise/' + avisId + '/beanstandung.eml?posten=' + encodeURIComponent(posten.join(',')) + '&an=' + encodeURIComponent(an),
  telekomBeanstandungEntwurf: (avisId: string, posten: string[], mailkontoId: string, an = '') => ruf<{ ok: boolean; klartext: string }>('/telekom/avise/' + avisId + '/entwurf', { method: 'POST', body: JSON.stringify({ posten, mailkonto_id: mailkontoId, an }) }),
  telekomAviseLesen: () => ruf<{ neu: number; fehler: string[]; klartext: string }>('/telekom/avise/lesen', { method: 'POST', body: '{}' }),
  telekomAvisHochladen: (pdf: string) => ruf<{ ok: boolean; klartext: string }>('/telekom/avise/hochladen', { method: 'POST', body: JSON.stringify({ pdf }) }),
  partnerzahlung: () => ruf<{ gruppen: any[]; ueberweisungen: any[]; revolut: any; vorschlaege: any[]; dubletten: any[]; zuletzt_abgleich_am: string | null }>('/partnerzahlung'),
  /** Suche über alle Rechnungen der Partner, jeder Zustand (.300). */
  partnerSuche: (suche: string) =>
    ruf<{ zeilen: any[] }>('/partnerzahlung/suche?suche=' + encodeURIComponent(suche.trim())),
  partnerEmpfaenger: (w: { subunternehmer_id: string; name: string; iban: string; bic?: string | null }) =>
    ruf<{ ok: boolean }>('/partnerzahlung/empfaenger', { method: 'POST', body: JSON.stringify(w) }),
  partnerAnRevolut: (ids: string[]) =>
    ruf<{ entwurf_id: string; ueberweisungen: number; rechnungen: number; summe: number; uebersprungen: string[] }>('/partnerzahlung/revolut', { method: 'POST', body: JSON.stringify({ ids }) }),
  partnerBezahlt: (ids: string[], datum: string) =>
    ruf<{ ok: boolean; markiert: number }>('/partnerzahlung/bezahlt', { method: 'POST', body: JSON.stringify({ ids, datum }) }),
  partnerKontoabgleich: () =>
    ruf<{ umsaetze: number; bezahlt: number; vorschlaege: number; gebuehren: number }>('/partnerzahlung/kontoabgleich', { method: 'POST', body: '{}' }),
  partnerVorschlag: (umsatzId: string, uebernehmen: boolean, rechnungen?: string[]) =>
    ruf<{ ok: boolean; bezahlt: number }>('/partnerzahlung/vorschlag/' + encodeURIComponent(umsatzId), { method: 'POST', body: JSON.stringify({ uebernehmen, rechnungen: rechnungen ?? null }) }),
  partnerAbschliessen: (ids: string[], grund: string) =>
    ruf<{ ok: boolean; abgeschlossen: number }>('/partnerzahlung/abschliessen', { method: 'POST', body: JSON.stringify({ ids, grund }) }),
  partnerKeineDublette: (id: string) =>
    ruf<{ ok: boolean }>('/partnerzahlung/dublette/' + id + '/nein', { method: 'POST', body: '{}' }),
  bankverbindungen: () => ruf<{ empfaenger: any[]; gemeinden: any[]; partner: any[] }>('/bankverbindungen'),
  bankverbindungNeu: (w: any) => ruf<{ ok: boolean; id: string }>('/bankverbindungen', { method: 'POST', body: JSON.stringify(w) }),
  bankverbindungAendern: (id: string, w: any) => ruf<{ ok: boolean }>('/bankverbindungen/' + id, { method: 'PATCH', body: JSON.stringify(w) }),
  bankverbindungBestaetigen: (id: string) => ruf<{ ok: boolean }>('/bankverbindungen/' + id + '/bestaetigen', { method: 'POST', body: '{}' }),
  bankverbindungEntfernen: (id: string) => ruf<{ ok: boolean }>('/bankverbindungen/' + id + '/entfernen', { method: 'POST', body: '{}' }),
  partnerAbgleichen: () =>
    ruf<{ geprueft: number; bezahlt: number; zurueck: number }>('/partnerzahlung/abgleichen', { method: 'POST', body: '{}' }),
  revolutSchluessel: (umgebung: 'produktion' | 'sandbox') =>
    ruf<{ zertifikat: string }>('/revolut/schluessel', { method: 'POST', body: JSON.stringify({ umgebung }) }),
  revolutClient: (client_id: string) =>
    ruf<{ zustimmung: string }>('/revolut/client', { method: 'POST', body: JSON.stringify({ client_id }) }),
  revolutZustimmung: () => ruf<{ zustimmung: string }>('/revolut/zustimmung'),
  revolutKonten: () => ruf<{ konten: { id: string; name: string; waehrung: string }[] }>('/revolut/konten'),
  revolutKonto: (konto_id: string) =>
    ruf<{ ok: boolean }>('/revolut/konto', { method: 'POST', body: JSON.stringify({ konto_id }) }),
  revolutTrennen: () => ruf<{ ok: boolean }>('/revolut/trennen', { method: 'POST', body: '{}' }),
  rechnungAendern: (id: string, w: any) =>
    ruf<any>('/rechnungen/' + id, { method: 'PATCH', body: JSON.stringify(w) }),
  // Eine Rechnung mit ihren Positionen, den Befunden und dem, was WIR für
  // denselben Auftrag anerkannt haben.
  rechnung: (id: string) => ruf<any>('/rechnungen/' + id),
  /** Das Papier einer Rechnung (.230); der Name hinten nur für den Browser. */
  rechnungAdresse: (id: string, dateiname?: string | null) =>
    '/api/rechnungen/' + id + '/datei' + (dateiname ? '/' + encodeURIComponent(dateiname) : ''),
  /** Eine Dublette löschen; der Server merkt sie sich (.230). */
  rechnungLoeschen: (id: string, grund: string) =>
    ruf<any>('/rechnungen/' + id, { method: 'DELETE', body: JSON.stringify({ grund }) }),
  // Die Positionen ersetzen die alte Liste ganz. Eine Rechnung ist ein Papier.
  /* EINEN BELEG NACHTRÄGLICH EINLESEN (21.09.2026).
     Marvin: „er hat nur die daten auf der rechnung nicht eingelesen." Die
     Rechnungen aus der Übernahme des alten Blattes hatten nie ein Papier in
     diesem Haus. Wer es hineinzieht, bekommt Auftrag, Nummer, Datum, Betrag
     und Positionen in einem Schritt. */
  rechnungBeleg: (id: string, datei: File, ueberschreiben = false) =>
    /* ÜBER `sendeDateiMitFeldern` UND NICHT ÜBER `ruf`. `ruf` setzt bei jedem
       Rumpf den Inhaltstyp auf JSON, und eine Sendung mit Dateien braucht die
       Trennmarke, die der Browser selbst schreibt. Und die Felder gehen VOR
       der Datei hinaus: der Server liest der Reihe nach. */
    sendeDateiMitFeldern<any>(
      '/rechnungen/' + id + '/beleg', datei,
      ueberschreiben ? { ueberschreiben: 'true' } : {}),
  /* FREIGABE ZUR ABRECHNUNG UND KORREKTUR (23.09.2026). Marvin: „die
     freigaben über ein menüpunkt bei den firmen anzeigen lassen, plus
     hinweis auf die glocke." */
  freigabenBereit: () => ruf<any>('/freigaben/bereit'),
  freigeben: (eintraege: { auftrag_id: string; subunternehmer_id: string;
                           gewerk_gruppe: string; mehr?: boolean; wie_eingereicht?: boolean; gutschrift_id?: string | null; hinweis?: string | null; nicht?: string[] }[], schonFreigegeben = false, grund: string | null = null) =>
    ruf<any>('/freigaben', { method: 'POST',
             body: JSON.stringify({ eintraege, schon_freigegeben: schonFreigegeben, ...(grund ? { grund } : {}) }) }),
  freigabeZuruecknehmen: (id: string, grund: string | null) =>
    ruf<any>('/freigaben/' + id + '/zuruecknehmen',
             { method: 'POST', body: JSON.stringify({ grund }) }),
  meineFreigaben: () => ruf<any>('/freigaben/meine'),
  /* DIE EINE SEITE ABRECHNUNG (.340): Stand, Abweichungen, Erinnerung,
     Zurückstellen, Löschen ohne Sperre, der Lauf von Hand, der Schalter je Firma. */
  abrechnungStand: () => ruf<any>('/abrechnung/stand'),
  abrechnungAbweichungen: (auftragId: string, subId: string, gewerk: string) =>
    ruf<{ abweichungen: any[]; zeilen: string[] }>('/abrechnung/abweichungen/' + auftragId + '/' + subId + '/' + gewerk),
  freigabenErinnern: (ids: string[]) => ruf<any>('/freigaben/erinnern', { method: 'POST', body: JSON.stringify({ ids }) }),
  freigabeErinnern: (id: string) =>
    ruf<any>('/freigaben/' + id + '/erinnern', { method: 'POST', body: '{}' }),
  rechnungZurueckstellen: (id: string, grund: string | null) =>
    ruf<any>('/rechnungen/' + id + '/zurueckstellen', { method: 'POST', body: JSON.stringify({ grund }) }),
  rechnungVorholen: (id: string) =>
    ruf<any>('/rechnungen/' + id + '/vorholen', { method: 'POST', body: '{}' }),
  rechnungGanzLoeschen: (id: string, grund: string | null) =>
    ruf<any>('/rechnungen/' + id + '/ganz', { method: 'DELETE', body: JSON.stringify({ grund }) }),
  abrechnungLauf: () => ruf<any>('/abrechnung/lauf', { method: 'POST', body: '{}' }),
  abrechnungFirmen: () => ruf<{ firmen: any[] }>('/abrechnung/firmen'),
  /* GBGS (.347): die NE3-Liste auf den Bauaufgaben und die Warteschlange des Roboters. */
  gbgsStand: () => ruf<any>('/gbgs/stand'),
  gbgsStandorte: (filter: string, suche: string | null) =>
    ruf<any[]>('/gbgs/standorte?filter=' + encodeURIComponent(filter) + (suche ? '&suche=' + encodeURIComponent(suche) : '')),
  gbgsWarteschlange: () => ruf<any[]>('/gbgs/warteschlange'),
  gbgsKatalog: () => ruf<any>('/gbgs/katalog'),
  gbgsLesen: () => ruf<any>('/gbgs/lesen', { method: 'POST', body: '{}' }),
  gbgsNochmal: (id: string) => ruf<any>('/gbgs/auftrag/' + id + '/nochmal', { method: 'POST', body: '{}' }),
  gbgsHandInWarteschlange: () => ruf<any>('/gbgs/hand-in-warteschlange', { method: 'POST', body: '{}' }),
  gbgsJetzt: (id: string) => ruf<any>('/gbgs/auftrag/' + id + '/jetzt', { method: 'POST', body: '{}' }),
  gbgsErledigt: (id: string) => ruf<any>('/gbgs/auftrag/' + id + '/erledigt', { method: 'POST', body: '{}' }),
  gbgsVerwerfen: (id: string) => ruf<any>('/gbgs/auftrag/' + id, { method: 'DELETE' }),
  gbgsWartegrund: (b: Record<string, unknown>) => ruf<any>('/gbgs/wartegrund', { method: 'POST', body: JSON.stringify(b) }),
  gbgsWartegrundVerlaengern: (b: Record<string, unknown>) => ruf<any>('/gbgs/wartegrund-verlaengern', { method: 'POST', body: JSON.stringify(b) }),
  gbgsTerminAngleichen: (auftragId: string) => ruf<any>('/gbgs/termin-angleichen/' + auftragId, { method: 'POST', body: '{}' }),
  gbgsStandortLesen: (auftragId: string) => ruf<any>('/gbgs/standort-lesen/' + auftragId, { method: 'POST', body: '{}' }),
  /* .598: dieselbe Kachel im NE4-Haus: lesen und den Termin ins Bulk-Portal geben, je NE4-Haus. */
  ne4HausGbgsLesen: (id: string) => ruf<any>('/ne4/auftraege/' + id + '/gbgs-lesen', { method: 'POST', body: '{}' }),
  ne4GbgsAngleichen: (id: string) => ruf<any>('/ne4/auftraege/' + id + '/gbgs-angleichen', { method: 'POST', body: '{}' }),
  gbgsWartegrundSammel: (b: Record<string, unknown>) => ruf<any>('/gbgs/wartegrund/sammel', { method: 'POST', body: JSON.stringify(b) }),
  gbgsVorlagen: () => ruf<any[]>('/gbgs/vorlagen'),
  gbgsVorlageAnlegen: (b: Record<string, unknown>) => ruf<any>('/gbgs/vorlagen', { method: 'POST', body: JSON.stringify(b) }),
  gbgsVorlageAendern: (id: string, b: Record<string, unknown>) => ruf<any>('/gbgs/vorlagen/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  gbgsVorlageLoeschen: (id: string) => ruf<any>('/gbgs/vorlagen/' + id, { method: 'DELETE' }),
  abrechnungVonSelbst: (firmaId: string, an: boolean) =>
    ruf<any>('/abrechnung/firma/' + firmaId + '/von-selbst', { method: 'POST', body: JSON.stringify({ an }) }),
  rechnungKorrekturZuruecknehmen: (id: string, kid: string, benachrichtigen = false) =>
    ruf<any>('/rechnungen/' + id + '/korrektur/' + kid + '/zuruecknehmen', { method: 'POST', body: JSON.stringify({ benachrichtigen }) }),
  rechnungKorrektur: (id: string, positionen: any[], satz: string | null) =>
    ruf<any>('/rechnungen/' + id + '/korrektur',
             { method: 'POST', body: JSON.stringify({ positionen, satz }) }),
  rechnungPositionen: (id: string, positionen: any[]) =>
    ruf<any>('/rechnungen/' + id + '/positionen',
             { method: 'PUT', body: JSON.stringify({ positionen }) }),
  /* DEN BESTAND NACHLESEN. Die PDF liegen in der Ablage; was fehlt, ist der
     Blick hinein. Eine Regel, die erst ab morgen gilt, holt den Bestand nie
     ein. */
  /* RECHNUNGEN OHNE POSITIONEN (.216): warum, und die Nummern der Firmen. */
  ohnePositionen: () => ruf<any>('/rechnungen/ohne-positionen'),
  fremdnummerZuordnen: (subunternehmer_id: string, nummer: string, oz: string) =>
    ruf<any>('/rechnungen/fremdnummern', { method: 'POST',
             body: JSON.stringify({ subunternehmer_id, nummer, oz }) }),
  fremdnummerLoeschen: (id: string) =>
    ruf<any>('/rechnungen/fremdnummern/' + id, { method: 'DELETE' }),
  positionenNachlesen: () =>
    ruf<any>('/rechnungen/positionen-nachlesen', { method: 'POST', body: JSON.stringify({}) }),
  rechnungenNachlesen: (neu = false) =>
    ruf<any>('/rechnungen/nachlesen',
             { method: 'POST',
               body: JSON.stringify({ hoechstens: neu ? 1000 : 400, neu }) }),
  telegram: (w: any) =>
    ruf<any>('/nachrichten/telegram', { method: 'POST', body: JSON.stringify(w) }),
  /* VORSCHAU UND VERSAND SIND DERSELBE AUFRUF, nur mit `senden` verschieden.
     Marvin, 02.09.2026: „beim versenden der telegramnachricht fehlt die
     vorschau". Zwei Wege über dieselbe Frage laufen beim ersten Nachtrag
     auseinander - und dann zeigt die Vorschau etwas anderes, als hinausgeht. */
  telegramAuftrag: (id: string, w: any) =>
    ruf<any>(`/auftraege/${id}/telegram`,
             { method: 'POST', body: JSON.stringify(w) }),
  kundenmail: (id: string) => ruf<any>(`/auftraege/${id}/kundenmail`),
  kundenmailSenden: (id: string, w: any) =>
    ruf<any>(`/auftraege/${id}/kundenmail`,
             { method: 'POST', body: JSON.stringify(w) }),

  benutzer: () => ruf<any[]>('/benutzer'),
  konfiguration: () => ruf<any[]>('/konfiguration'),
  // Der Bauplan liefert seit dem 23.08.2026 zwei Dinge: die Module UND die
  // Datenherkunft. Der Hinweis auf der Seite haengt an der Herkunft.
  bauplan: (bereich?: string | null) =>
    ruf<{ module: Bauteil[] }>('/bauplan' + (bereich ? '?bereich=' + encodeURIComponent(bereich) : '')),
  bereiche: () => ruf<Bereich[]>('/bereiche'),

  // --- NE4 (.333) ---
  ne4Firmen: () => ruf<any[]>('/ne4/firmen'),
  benutzerNe4: (id: string, b: { firma_id: string | null; team_id: string | null }) => ruf<any>('/benutzer/' + id + '/ne4', { method: 'PUT', body: JSON.stringify(b) }),
  ne4FirmaAnlegen: (b: any) => ruf<any>('/ne4/firmen', { method: 'POST', body: JSON.stringify(b) }),
  ne4FirmaAendern: (id: string, b: any) => ruf<any>('/ne4/firmen/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  ne4FirmaZugaenge: (id: string, benutzer: string[]) =>
    ruf<any>('/ne4/firmen/' + id + '/zugaenge', { method: 'PUT', body: JSON.stringify({ benutzer }) }),
  ne4Teams: () => ruf<any[]>('/ne4/teams'),
  ne4TeamAnlegen: (b: any) => ruf<any>('/ne4/teams', { method: 'POST', body: JSON.stringify(b) }),
  ne4TeamAendern: (id: string, b: any) => ruf<any>('/ne4/teams/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  ne4Personen: () => ruf<any[]>('/ne4/personen'),
  ne4GbgsRessourcen: () => ruf<{ bid: number; name: string; faehigkeit: string | null }[]>('/ne4/gbgs-ressourcen'),
  ne4Ne3Firmen: () => ruf<any[]>('/ne4/firmen/ne3'),
  ne4FirmaAusNe3: (subunternehmer_id: string) => ruf<{ id: string; team_id: string | null; verknuepft: boolean }>('/ne4/firmen/aus-ne3', { method: 'POST', body: JSON.stringify({ subunternehmer_id }) }),
  ne4PersonAendern: (id: string, b: { name?: string; aktiv?: boolean; gbgs_ressource_bid?: number | null }) => ruf<any>('/ne4/personen/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  ne4OrtZurPlz: (plz: string) => ruf<{ ort: string | null }>('/ne4/ort?plz=' + encodeURIComponent(plz)),
  ne4PersonAnlegen: (b: { firma_id: string; name: string }) => ruf<{ id: string; name: string }>('/ne4/personen', { method: 'POST', body: JSON.stringify(b) }),
  ne4Konfiguration: () => ruf<any[]>('/ne4/konfiguration'),
  ne4KonfigurationSetzen: (schluessel: string, wert: string | null) =>
    ruf<any>('/ne4/konfiguration/' + encodeURIComponent(schluessel), { method: 'PUT', body: JSON.stringify({ wert }) }),
  ne4Benutzer: () => ruf<any[]>('/ne4/benutzer'),
  // NE4 L2 bis L5 (.334)
  ne4ImportFelder: () => ruf<{ felder: any[] }>('/ne4/import/felder'),
  ne4ImportVorschau: (datei: File) => sendeDateien<any>('/ne4/import/vorschau', [datei]),
  ne4Import: (datei: File, zuordnung: Record<string, string>) => sendeDateiMitFeldern<any>('/ne4/import', datei, { zuordnung: JSON.stringify(zuordnung) }),
  ne4Importe: () => ruf<any[]>('/ne4/importe'),
  // .338: Auftrag von Hand, Karte, Demodaten
  ne4AuftragAnlegen: (b: any) => ruf<any>('/ne4/auftraege', { method: 'POST', body: JSON.stringify(b) }),
  ne4Karte: () => ruf<any>('/ne4/karte'),
  ne3Karte: () => ruf<any>('/karte'),
  ne4KartenbildAdresse: (lat: number, lon: number, zoom: number, breite: number, hoehe: number, plz?: string | null) =>
    `/api/ne4/kartenbild?${new URLSearchParams({ lat: String(lat), lon: String(lon), zoom: String(zoom), breite: String(breite), hoehe: String(hoehe), ...(plz ? { plz } : {}) }).toString()}`,
  ne4Auftraege: (p: URLSearchParams) => ruf<any>('/ne4/auftraege?' + p.toString()),
  ne4GbgsLesen: () => ruf<{ klartext: string }>('/ne4/gbgs-lesen', { method: 'POST', body: '{}' }),
  ne4Auftrag: (id: string) => ruf<any>('/ne4/auftraege/' + id),
  ne4AuftragAendern: (id: string, b: any) => ruf<any>('/ne4/auftraege/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  ne4Vorschlaege: (id: string) => ruf<any>('/ne4/auftraege/' + id + '/vorschlaege'),
  ne4Plaetze: (id: string, von?: string, bis?: string) => ruf<any>('/ne4/auftraege/' + id + '/plaetze' + (von ? `?von=${von}&bis=${bis ?? ''}` : '')),
  /* .590: Meine Aufträge (Firma und Team). */
  ne4FirmaAuftraege: () => ruf<any[]>('/ne4/firma/auftraege'),
  ne4FirmaAuftrag: (id: string) => ruf<any>('/ne4/firma/auftraege/' + id),
  ne4FirmaEintrag: (id: string, text: string) => ruf<any>('/ne4/firma/auftraege/' + id + '/eintrag', { method: 'POST', body: JSON.stringify({ text }) }),
  ne4FirmaDateien: async (id: string, ordner: string, dateien: File[]) => {
    for (const d of dateien) await sendeDateiMitFeldern<any>('/ne4/firma/auftraege/' + id + '/dateien', d, { ordner });
    return { ok: true };
  },
  ne4Kontakt: (id: string, b: any) => ruf<any>('/ne4/auftraege/' + id + '/kontakt', { method: 'POST', body: JSON.stringify(b) }),
  ne4KontaktAendern: (kontaktId: string, text: string) => ruf<any>('/ne4/kontakte/' + kontaktId, { method: 'PATCH', body: JSON.stringify({ text }) }),
  ne4KontaktLoeschen: (kontaktId: string) => ruf<any>('/ne4/kontakte/' + kontaktId, { method: 'DELETE' }),
  /* .620: Status von Hand; Meldung an das NE4-Postfach der Telekom. */
  ne4Zustand: (id: string, zustand: string, grund: string | null) => ruf<any>('/ne4/auftraege/' + id + '/zustand', { method: 'POST', body: JSON.stringify({ zustand, grund }) }),
  ne4TelekomVorlagen: (id: string) => ruf<any>('/ne4/auftraege/' + id + '/telekom-meldung'),
  ne4TelekomMelden: (id: string, b: { vorlage: string; betreff: string; text: string; dateien: string[] }) => ruf<any>('/ne4/auftraege/' + id + '/telekom-meldung', { method: 'POST', body: JSON.stringify(b) }),
  ne4Rueckgabe: (id: string, grund: string) => ruf<any>('/ne4/auftraege/' + id + '/rueckgabe', { method: 'POST', body: JSON.stringify({ grund }) }),
  ne4Wiederaufnehmen: (id: string) => ruf<any>('/ne4/auftraege/' + id + '/wiederaufnehmen', { method: 'POST', body: '{}' }),
  ne4TerminBuchen: (id: string, teamtag_id: string, beginn: string) => ruf<any>('/ne4/auftraege/' + id + '/termin', { method: 'POST', body: JSON.stringify({ teamtag_id, beginn }) }),
  ne4TerminAbsagen: (id: string, grund: string) => ruf<any>('/ne4/termine/' + id + '/absagen', { method: 'POST', body: JSON.stringify({ grund }) }),
  ne4Anrufliste: () => ruf<any>('/ne4/anrufliste'),
  ne4Tagesplan: (tag?: string, team?: string) => ruf<any>('/ne4/tagesplan?' + new URLSearchParams({ ...(tag ? { tag } : {}), ...(team ? { team } : {}) }).toString()),
  ne4MeineTermine: () => ruf<any[]>('/ne4/meine-termine'),
  ne4Fertig: (id: string, b: any) => ruf<any>('/ne4/termine/' + id + '/fertig', { method: 'POST', body: JSON.stringify(b) }),
  /* .495: Gebäudeaufträge */
  ne4Kalkulation: (etagen: number | string, we: number | string) => ruf<any>('/ne4/kalkulation?etagen=' + encodeURIComponent(String(etagen)) + '&we=' + encodeURIComponent(String(we))),
  ne4BlockBuchen: (id: string, b: { team_id: string; start_tag: string; tage?: number | string | null }) => ruf<any>('/ne4/auftraege/' + id + '/block', { method: 'POST', body: JSON.stringify(b) }),
  ne4BlockAbsagen: (id: string, grund: string) => ruf<any>('/ne4/auftraege/' + id + '/block/absagen', { method: 'POST', body: JSON.stringify({ grund }) }),
  ne4HausMail: (id: string, schluessel: 'haus_aushang' | 'haus_bestaetigung', email?: string, eigen?: { betreff?: string; text?: string }) => ruf<any>('/ne4/auftraege/' + id + '/hausmail', { method: 'POST', body: JSON.stringify({ schluessel, email, ...(eigen ?? {}) }) }),
  ne4HausMailVorschau: (id: string, schluessel: string, email?: string) => ruf<any>('/ne4/auftraege/' + id + '/hausmail/vorschau?schluessel=' + encodeURIComponent(schluessel) + (email ? '&email=' + encodeURIComponent(email) : '')),
  ne4BewohnerInformiert: (id: string, text?: string) => ruf<any>('/ne4/auftraege/' + id + '/bewohner-informiert', { method: 'POST', body: JSON.stringify({ text }) }),
  ne4WohnungAnlegen: (id: string, b: any) => ruf<any>('/ne4/auftraege/' + id + '/wohnungen', { method: 'POST', body: JSON.stringify(b) }),
  ne4WohnungAendern: (id: string, b: any) => ruf<any>('/ne4/wohnungen/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  ne4WohnungWeg: (id: string) => ruf<any>('/ne4/wohnungen/' + id, { method: 'DELETE' }),
  /* .515: Termine je Connect aus dem Terminfenster des Hauses. */
  ne4ConnectTermine: (id: string, b: { tag: string; tage?: string[]; ganztags?: boolean; von: string; bis: string; wohnungen?: string[] }) => ruf<any>('/ne4/auftraege/' + id + '/connect-termine', { method: 'POST', body: JSON.stringify(b) }),
  ne4Belegung: (von: string, bis: string, team?: string, plz?: string | null) => ruf<{ von: string; bis: string; teams: any[]; tage: any[]; feiertage?: { tag: string; name: string }[] }>(`/ne4/belegung?von=${von}&bis=${bis}${team ? `&team=${team}` : ''}${plz ? `&plz=${plz}` : ''}`),
  ne4Haustermin: (id: string, b: { team_id: string; tage: string[]; ganztags: boolean; von: string; bis: string; wohnungen: string[]; mail?: boolean }) => ruf<any>('/ne4/auftraege/' + id + '/haustermin', { method: 'POST', body: JSON.stringify(b) }),
  ne4Haus: (id: string, b: Record<string, any>) => ruf<any>('/ne4/auftraege/' + id + '/haus', { method: 'PUT', body: JSON.stringify(b) }),
  ne4AuskundungLesen: (id: string) => ruf<any>('/ne4/auftraege/' + id + '/auskundung-lesen', { method: 'POST', body: '{}' }),
  ne4ConnectTermineAufheben: (id: string, wohnungen: string[]) => ruf<any>('/ne4/auftraege/' + id + '/connect-termine/aufheben', { method: 'POST', body: JSON.stringify({ wohnungen }) }),
  /* .509: Verwaltung, Telefonat */
  ne4Verwaltungen: () => ruf<any[]>('/ne4/verwaltungen'),
  ne4VerwaltungAnlegen: (b: any) => ruf<any>('/ne4/verwaltungen', { method: 'POST', body: JSON.stringify(b) }),
  ne4VerwaltungAendern: (id: string, b: any) => ruf<any>('/ne4/verwaltungen/' + id, { method: 'PUT', body: JSON.stringify(b) }),
  ne4HausVerwaltung: (id: string, b: any) => ruf<any>('/ne4/auftraege/' + id + '/verwaltung', { method: 'POST', body: JSON.stringify(b) }),
  ne4Antworten: (id: string, antworten: Record<string, string | null>) => ruf<any>('/ne4/auftraege/' + id + '/antworten', { method: 'PUT', body: JSON.stringify({ antworten }) }),
  ne4Unterlage: (id: string, datei: File, art = 'auskundung') => sendeDateiMitFeldern<any>('/ne4/auftraege/' + id + '/unterlagen', datei, { art }),
  ne4AushangHochladen: (datei: File) => sendeDateien<any>('/ne4/verwaltung/aushang', [datei]),
  ne4NichtErledigt: (id: string, grund: string, mehr?: Record<string, unknown>) => ruf<any>('/ne4/termine/' + id + '/nicht-erledigt', { method: 'POST', body: JSON.stringify({ ...(mehr ?? {}), grund }) }),
  /* .592: Statusablauf, Ordner, Verfügbarkeit (NE4 wie NE3). */
  ne4Begonnen: (id: string) => ruf<any>('/ne4/termine/' + id + '/begonnen', { method: 'POST', body: '{}' }),
  ne4Verzoegerung: (id: string, grund: string, bis: string | null) => ruf<any>('/ne4/termine/' + id + '/verzoegerung', { method: 'POST', body: JSON.stringify({ grund, bis }) }),
  ne4Ordner: (id: string) => ruf<{ dateien: any[]; ordner: { name: string; pflicht: boolean; zweck: string }[] }>('/ne4/auftraege/' + id + '/ordner'),
  ne4OrdnerHochladen: (id: string, ordner: string, datei: File) => sendeDateiMitFeldern<any>('/ne4/auftraege/' + id + '/ordner', datei, { ordner }),
  ne4FirmaDatei: (id: string, ordner: string, datei: File) => sendeDateiMitFeldern<any>('/ne4/firma/auftraege/' + id + '/dateien', datei, { ordner }),
  ne4Verfuegbarkeit: (von: string, bis: string, team?: string) => ruf<{ teams: any[]; team: string | null; zeilen: any[] }>(`/ne4/verfuegbarkeit?von=${von}&bis=${bis}${team ? `&team=${team}` : ''}`),
  ne4VerfuegbarkeitSpeichern: (team_id: string, tage: { datum: string; stand: 'frei' | 'urlaub' | 'weg' }[]) => ruf<any>('/ne4/verfuegbarkeit', { method: 'POST', body: JSON.stringify({ team_id, tage }) }),
  ne4Dateien: (id: string, dateien: File[]) => sendeDateien<any>('/ne4/termine/' + id + '/dateien', dateien),
  ne4Gebiete: () => ruf<any[]>('/ne4/gebiete'),
  ne4Plz: () => ruf<any[]>('/ne4/plz'),
  ne4GebietAnlegen: (b: any) => ruf<any>('/ne4/gebiete', { method: 'POST', body: JSON.stringify(b) }),
  ne4GebietAendern: (id: string, b: any) => ruf<any>('/ne4/gebiete/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  ne4GebieteNeu: () => ruf<any>('/ne4/gebiete/neu-bilden', { method: 'POST', body: '{}' }),
  ne4Teamtage: (von?: string) => ruf<any>('/ne4/teamtage' + (von ? '?von=' + von : '')),
  ne4TeamtagSetzen: (b: any) => ruf<any>('/ne4/teamtage', { method: 'POST', body: JSON.stringify(b) }),
  ne4TeamtagLoeschen: (id: string) => ruf<any>('/ne4/teamtage/' + id, { method: 'DELETE' }),
  ne4Mailtexte: () => ruf<any[]>('/ne4/mailtexte'),
  ne4MailtextSetzen: (s: string, b: any) => ruf<any>('/ne4/mailtexte/' + s, { method: 'PUT', body: JSON.stringify(b) }),
  ne4Automatik: () => ruf<any>('/ne4/automatik'),
  ne4AutomatikJetzt: (s: string) => ruf<any>('/ne4/automatik/' + s + '/jetzt', { method: 'POST', body: '{}' }),
  ne4AutomatikAktiv: (s: string, aktiv: boolean) => ruf<any>('/ne4/automatik/' + s + '/aktiv', { method: 'POST', body: JSON.stringify({ aktiv }) }),
  ne4KonfigurationGeheim: (s: string, wert: string | null) => ruf<any>('/ne4/konfiguration-geheim/' + encodeURIComponent(s), { method: 'PUT', body: JSON.stringify({ wert }) }),
  ne4Gbgs: (alle?: boolean) => ruf<any>('/ne4/gbgs' + (alle ? '?alle=ja' : '')),
  ne4GbgsErledigt: (id: string, ergebnis: string | null, verworfen = false) => ruf<any>('/ne4/gbgs/' + id + '/erledigt', { method: 'POST', body: JSON.stringify({ ergebnis, verworfen }) }),
  ne4Zahlen: (monat?: string) => ruf<any>('/ne4/zahlen' + (monat ? '?monat=' + monat : '')),
  /** .486: Anrufe je Mitarbeiter (nur mit ne4.anrufe.auswerten). */
  ne4Anrufe: (von?: string, bis?: string) => ruf<any>('/ne4/anrufe?' + new URLSearchParams({ ...(von ? { von } : {}), ...(bis ? { bis } : {}) }).toString()),
  ne4Positionen: () => ruf<any[]>('/ne4/positionen'),
  ne4PositionAnlegen: (b: any) => ruf<any>('/ne4/positionen', { method: 'POST', body: JSON.stringify(b) }),
  ne4PositionAendern: (id: string, b: any) => ruf<any>('/ne4/positionen/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  ne4Abrechnung: (monat?: string) => ruf<any>('/ne4/abrechnung' + (monat ? '?monat=' + monat : '')),
  ne4AbrechnungAbschliessen: (monat: string) => ruf<any>('/ne4/abrechnung/abschliessen', { method: 'POST', body: JSON.stringify({ monat }) }),
  termine: (p: URLSearchParams) => ruf<any>('/termine?' + p.toString()),

  /* ECHTE SPERREN: eine Firma kann an diesem Tag nicht.
     Die Tabelle steht seit Migration 001 im Schema und wurde seit dem
     Wegfall der Übernahme (062) von KEINEM Weg mehr gefüllt - der
     Wochenplan las eine Tabelle, die niemand schreibt. Migration 101. */
  /* `sperreAnlegen` und `sperreWeg` sind am 18.09.2026 gefallen. Ein
     Zeitfenster ist gebucht oder frei; was einen ganzen Tag betrifft, geht
     über die Verfügbarkeit. */
  behoerden: () => ruf<any>('/behoerden'),
  abrechnung: () => ruf<any>('/abrechnung'),
  /* Der Zeitraum ist freiwillig. Ohne ihn nimmt der Server die letzten zwölf
   * Monate und schreibt beide Enden in die Antwort: die Oberfläche soll nicht
   * raten müssen, welchen Zeitraum sie gerade anzeigt. */
  /** Leistungspositionen, gegliedert (.230). */
  auswertungLeistungen: (von: string, bis: string, nach: string) =>
    ruf<any>('/auswertung/leistungen?' + new URLSearchParams({ von, bis, nach }).toString()),
  auswertung: (von?: string, bis?: string) => {
    const p = new URLSearchParams();
    if (von) p.set('von', von);
    if (bis) p.set('bis', bis);
    const s = p.toString();
    return ruf<any>('/auswertung' + (s ? '?' + s : ''));
  },
  /* EINE ZAHL OHNE WEG DAHINTER IST EINE BEHAUPTUNG. Das hier ist der Weg:
   * die Aufträge, die hinter einer angeklickten Zahl stehen. */
  auswertungAuftraege: (w: Record<string, string | undefined>) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(w)) if (v) p.set(k, v);
    return ruf<any>('/auswertung/auftraege?' + p.toString());
  },

  /* DIE POSITIONSAUSWERTUNG.
   *
   * ORIGINALTEXT MARVIN (03.09.2026)
   *   „ich will in der Auswertung wissen, einmal nachher die Positionen
   *    einzeln bzw. gesamt auswerten können über einen Zeitraum für die
   *    nächste Vertragsverhandlung, um zu sehen, wie viel Volumen wurde
   *    abgerufen an Menge und an Preis über welche Position."
   *
   * DIE FILTER GEHEN IN BEIDE WEGE GLEICH. Zwei Fassungen davon liefen beim
   * ersten Nachtrag auseinander, und dann addiert sich das Aufgeklappte nicht
   * mehr zur Summe darüber - danach glaubt niemand mehr einer von beiden. */
  auswertungPositionen: (w: Record<string, string | undefined>) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(w)) if (v) p.set(k, v);
    return ruf<any>('/auswertung/positionen?' + p.toString());
  },
  /* Die Aufschlüsselung hinter EINER Position: erst die Regionen, dann jede
     einzelne Baustelle. */
  auswertungPositionDetail: (
    oz: string, w: Record<string, string | undefined>
  ) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(w)) if (v) p.set(k, v);
    return ruf<any>(`/auswertung/positionen/${encodeURIComponent(oz)}?`
                    + p.toString());
  },
  /* WAS VON EINEM AUFTRAG ÜBRIG BLEIBT.
   *
   * ORIGINALTEXT MARVIN (06.09.2026)
   *   „was dann faktisch pro auftrag bei mir bleibt und das kann ich dann
   *    alles auswerten."
   *
   * Der Zeitraum entscheidet, WELCHE Aufträge in der Liste stehen - nicht,
   * mit welchen Beträgen: der Rest ist eine Bestandszahl. Die Begründung
   * steht im Kopf von Migration 160. */
  auswertungGeldAuftraege: (w: Record<string, string | undefined>) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(w)) if (v) p.set(k, v);
    return ruf<any>('/auswertung/geld/auftraege?' + p.toString());
  },

  // ---- Die Rahmenverträge und die Preise darin ----------------------------
  rahmenvertraege: () => ruf<any>('/rahmenvertraege'),
  rahmenvertragSpeichern: (v: any) =>
    ruf<any>('/rahmenvertraege',
             { method: 'POST', body: JSON.stringify(v) }),
  rahmenvertragPreise: (id: string) =>
    ruf<any>(`/rahmenvertraege/${id}/preise`),
  rahmenvertragPreisSetzen: (id: string, p: any) =>
    ruf<any>(`/rahmenvertraege/${id}/preise`,
             { method: 'POST', body: JSON.stringify(p) }),
  rahmenvertragPreisWeg: (id: string, preis: string) =>
    ruf<any>(`/rahmenvertraege/${id}/preise/${preis}`, { method: 'DELETE' }),
  auftragRahmenvertrag: (auftrag: string, rahmenvertrag: string | null) =>
    ruf<any>(`/auftraege/${auftrag}/rahmenvertrag`,
             { method: 'PUT',
               body: JSON.stringify({ rahmenvertrag_id: rahmenvertrag }) }),
  // Die Kapazitätsmeldung. Alles, was der Bildschirm braucht, in EINER
  // Antwort: früher lief je Klick ein Aufruf.
  kapazitaet: (wochen?: number) =>
    ruf<any>('/kapazitaet' + (wochen ? '?wochen=' + wochen : '')),
  kapazitaetMelden: (w: any) =>
    ruf<any>('/kapazitaet/meldung', { method: 'POST', body: JSON.stringify(w) }),
  kapazitaetZuschlag: (w: any) =>
    ruf<any>('/kapazitaet/zuschlag', { method: 'POST', body: JSON.stringify(w) }),
  // Der Bereich gehoert dazu: uebertragen wird Bereich fuer Bereich, und ein
  // Haken je Woche hakt die uebrigen Bereiche mit ab, die nie uebertragen
  // wurden.
  kapazitaetPortal: (jahr: number, kalenderwoche: number, bereich: string) =>
    ruf<any>('/kapazitaet/portal',
             { method: 'POST',
               body: JSON.stringify({ jahr, kalenderwoche, bereich }) }),
  // AUF WEITERE WOCHEN KOPIEREN. Die Kapazität einer Firma ändert sich nicht
  // wöchentlich, die Meldung muss aber jede Woche dastehen. Wer fünf
  // Positionen mal drei Bereiche mal zwölf Wochen tippt, tippt sich Fehler.
  kapazitaetKopieren: (w: any) =>
    ruf<any>('/kapazitaet/kopieren',
             { method: 'POST', body: JSON.stringify(w) }),
  // DIE MELDUNG GEHT JE BEREICH, und welcher Auftrag zu welchem Bereich
  // gehört, sagt allein `onkz_bereich`. Bis .62 hatte diese Tabelle keinen
  // Pflegeweg und war leer.
  kapazitaetBereich: (w: { onkz: string; bereich: string; ort?: string }) =>
    ruf<any>('/kapazitaet/bereich',
             { method: 'POST', body: JSON.stringify(w) }),
  kapazitaetBereichWeg: (onkz: string) =>
    ruf<any>('/kapazitaet/bereich/' + encodeURIComponent(onkz),
             { method: 'DELETE' }),
  // OHNE KOORDINATEN GIBT ES WEDER KARTE NOCH LAP_MAPS NOCH UMGEBUNGSBILDER.
  //
  // GESUCHT WIRD EINE ANSCHRIFT UND NICHT ZWEI ZAHLEN. Marvin, 10.09.2026:
  // „die eingabe von koordinaten kannst du bei beiden karten komplett
  // entfernen. wir ersetzen die eingabe damit." Ohne Rumpf sucht der Server
  // die Anschrift des Auftrags; mit Straße und Hausnummer sucht er die
  // eingetippte. PLZ und Ort stehen fest, sie kommen aus dem Auftrag.
  /* VORSCHLÄGE IM UMKREIS (.245), wenn die Anschrift nicht gefunden wird. */
  umkreis: (auftragId: string) => ruf<any>('/auftraege/' + auftragId + '/umkreis'),
  koordinatenSetzen: (auftragId: string,
                      b?: { strasse?: string; hausnummer?: string; ortsteil?: string; vorschlag?: 'bezug' }) =>
    ruf<any>('/auftraege/' + auftragId + '/koordinaten',
             { method: 'POST', body: JSON.stringify(b ?? {}) }),
  nachrichten: () => ruf<any>('/nachrichten'),
  // --- Zweiter Faktor ---
  faktorStand: () => ruf<any>('/faktor/stand'),
  faktorEinrichten: (neu = false) => ruf<any>('/faktor/einrichten',
    neu ? { method: 'POST', body: JSON.stringify({ neu: true }) } : { method: 'POST' }),
  faktorBestaetigen: (code: string) =>
    ruf<any>('/faktor/bestaetigen', { method: 'POST', body: JSON.stringify({ code }) }),
  faktorPruefen: (code: string, geraetMerken: boolean) =>
    ruf<any>('/faktor/pruefen',
             { method: 'POST', body: JSON.stringify({ code, geraetMerken }) }),
  faktorWiederherstellen: (code: string) =>
    ruf<any>('/faktor/wiederherstellen',
             { method: 'POST', body: JSON.stringify({ code }) }),
  /* PASSKEY. Vier Wege, zwei Paare: anlegen/bestätigen für die Einrichtung,
     anmelden/prüfen für den zweiten Schritt der Anmeldung. Die AUFGABE steht
     dazwischen auf dem Server - der Browser reicht nur durch, was sein Gerät
     unterschrieben hat. */
  passkeyAnlegenStart: () =>
    ruf<any>('/faktor/passkey/anlegen', { method: 'POST', body: '{}' }),
  passkeyBestaetigen: (d: Record<string, string>) =>
    ruf<any>('/faktor/passkey/bestaetigen',
             { method: 'POST', body: JSON.stringify(d) }),
  passkeyAnmeldenStart: () =>
    ruf<any>('/faktor/passkey/anmelden', { method: 'POST', body: '{}' }),
  passkeyPruefen: (d: Record<string, unknown>) =>
    ruf<any>('/faktor/passkey/pruefen',
             { method: 'POST', body: JSON.stringify(d) }),
  faktorEntfernen: (id: string) => ruf<any>('/faktor/' + id, { method: 'DELETE' }),
  faktorGeraete: () => ruf<any[]>('/faktor/geraete'),
  faktorGeraeteWiderrufen: () =>
    ruf<any>('/faktor/geraete/widerrufen', { method: 'POST' }),
  /* Seit Migration 042 kommt hier nicht nur die Liste, sondern auch der
     Stand der Pflicht und wen ihr Einschalten aussperren würde. */
  faktorUebersicht: () => ruf<any>('/faktor/uebersicht'),
  faktorZuruecksetzen: (benutzerId: string) =>
    ruf<any>('/faktor/zuruecksetzen/' + benutzerId, { method: 'POST', body: '{}' }),
  faktorVersuche: () => ruf<any[]>('/faktor/versuche'),

  // --- Rechteverwaltung ---
  module: () => ruf<any[]>('/module'),
  gruppen: () => ruf<any[]>('/gruppen'),
  gruppeAnlegen: (b: { schluessel: string; name: string; beschreibung?: string; bereich?: string | null }) =>
    ruf<any>('/gruppen', { method: 'POST', body: JSON.stringify(b) }),
  gruppeAendern: (id: string, b: { name?: string; beschreibung?: string; bereich?: string | null }) =>
    ruf<any>('/gruppen/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  gruppeLoeschen: (id: string) =>
    ruf<any>('/gruppen/' + id, { method: 'DELETE' }),
  gruppeRechte: (id: string, rechte: string[]) =>
    ruf<any>('/gruppen/' + id + '/rechte',
             { method: 'PUT', body: JSON.stringify({ rechte }) }),
  /* WELCHE MENÜPUNKTE DIESE GRUPPE SIEHT. Geschickt wird, was ERSCHEINEN
     soll; gespeichert wird die Ausnahme. Die Umdrehung steht im Server und
     nur dort - zweimal wäre einmal zu viel. */
  gruppeMenue: (id: string, module: string[]) =>
    ruf<any>('/gruppen/' + id + '/menue',
             { method: 'PUT', body: JSON.stringify({ module }) }),
  einBenutzer: (id: string) => ruf<any>('/benutzer/' + id),
  /* EINEN ZUGANG ANLEGEN. Bis zum 27.08.2026 gab es diesen Weg nicht: jede
     Benutzerzeile stammte aus der Übernahme, und ein neuer Monteur
     einer Partnerfirma hätte von Hand in die Datenbank geschrieben werden
     müssen. Die Firma entscheidet, was er sieht - deshalb steht sie hier im
     Formular und nicht in einem zweiten Schritt. */
  benutzerAnlegen: (b: any) =>
    ruf<any>('/benutzer', { method: 'POST', body: JSON.stringify(b) }),
  benutzerAendern: (id: string, b: any) =>
    ruf<any>('/benutzer/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  /* Löschen (.461): aus der Liste, keine Anmeldung mehr, Mailadresse frei; der Name bleibt an seinen Einträgen. */
  benutzerLoeschen: (id: string) => ruf<any>('/benutzer/' + id, { method: 'DELETE' }),
  /* DER SCHALTER FÜR DIE WARTESEITE, und zwar ein EIGENER Weg.
     Marvin, 03.09.2026: „kannst du noch eine
     willkommens/Warteseite/gesperrtseite einbinden, die ich per schalter bei
     en benutzern aktivieren/deaktievieren kann." Beim Freischalten geht eine
     Mail hinaus - eine Nebenwirkung an einem beliebigen Feldwechsel
     überrascht beim zwanzigsten Mal. */
  benutzerFreischaltung: (id: string, wartet: boolean) =>
    ruf<any>(`/benutzer/${id}/freischaltung`,
             { method: 'PUT', body: JSON.stringify({ wartet }) }),
  /* Was auf der Warteseite steht. Braucht kein Recht: angemeldet genügt. */
  warteseite: () => ruf<any>('/warteseite'),
  /* Wen träfe das Einschalten der 2FA-Pflicht? Seit Migration 042. Ein
     Schalter, dessen Wirkung man vorher nicht sieht, wird entweder nie
     umgelegt oder einmal zu früh. */
  faktorpflicht: () => ruf<any>('/benutzer/faktorpflicht'),
  /* EIN LEISTUNGSVERZEICHNIS AUS EINER DATEI.
     Marvin, 31.08.2026: „der muss zumindest mal dann die Datei einlesen
     können." Ohne `anwenden` wird nur gerechnet und nichts geschrieben -
     derselbe Aufbau wie beim Telekom-Import. */
  lvEinlesen: (datei: File, anwenden = false) =>
    sendeDateien<any>(`/lv/einlesen?anwenden=${anwenden ? 'ja' : 'nein'}`,
                      [datei]),
  benutzerGruppen: (id: string, gruppen: string[]) =>
    ruf<any>('/benutzer/' + id + '/gruppen',
             { method: 'PUT', body: JSON.stringify({ gruppen }) }),
  benutzerRechte: (id: string,
                   ausnahmen: { recht: string; erlaubt: boolean; grund: string }[]) =>
    ruf<any>('/benutzer/' + id + '/rechte',
             { method: 'PUT', body: JSON.stringify({ ausnahmen }) }),
  // Login als ein anderer Benutzer. Ohne Grund, ohne eigenes Protokoll:
  // Marvin am 23.08.2026, „das ist zu tief gedacht."
  loginAls: (benutzerId: string) =>
    ruf<any>('/login-als', { method: 'POST', body: JSON.stringify({ benutzerId }) }),
  zurueckZuMir: () => ruf<any>('/zurueck-zu-mir', { method: 'POST' }),
  /* Ein Passwort für einen anderen Benutzer setzen.
   *
   * ORIGINALTEXT MARVIN (24.08.2026)
   *   „Niemand bekommt einen extra Link. Alle loggen sich mit ihren
   *    Zugangsdaten ein und aufgrund der Zuordnung und Rechten sieht er dann
   *    das was er sehen muss."
   *
   * Hier stand `anmeldelink`. */
  passwortSetzen: (benutzerId: string, passwort: string) =>
    ruf<{ name: string; beendeteSitzungen: number }>(
      `/benutzer/${benutzerId}/passwort`,
      { method: 'POST', body: JSON.stringify({ passwort }) }),
  /* Sein eigenes ändern. Das alte gehört dazu, auch wenn man angemeldet ist:
   * sonst genügt eine unbeaufsichtigte Minute am offenen Rechner. */
  meinPasswort: (alt: string, neu: string) =>
    ruf<{ beendeteSitzungen: number }>(
      '/ich/passwort', { method: 'POST', body: JSON.stringify({ alt, neu }) }),

  /* Die Passwortvorgabe, zum Anzeigen. OHNE Anmeldung - der
     Einladungsbildschirm braucht sie, und dort ist noch niemand angemeldet.
     Marvin, 27.08.2026: „vorgabe zum passwort geben wir vor nach den heutigen
     sicherheitsrichtlinien. das muss auch angezeigt werden bei jeder
     passwortvergabe." */
  passwortregeln: () => ruf<any>('/passwortregeln'),

  /* Eine Einladung zum Passwortsetzen (erneut) versenden. Migration 054.
     Der Link darin MELDET NICHT AN - er setzt genau ein Passwort und ist
     danach verbraucht. Der Anmeldelink aus Migration 030 ist etwas anderes
     und bleibt abgeschafft. */
  einladungSenden: (benutzerId: string) =>
    ruf<any>(`/benutzer/${benutzerId}/einladung`, { method: 'POST' }),
  /* Diese beiden gehen OHNE Anmeldung. Sie sind die einzigen ausser dem
     Anmelden selbst. */
  einladungStand: (token: string) =>
    ruf<any>('/einladung/' + encodeURIComponent(token)),
  einladungSetzen: (token: string, passwort: string) =>
    ruf<any>('/einladung/' + encodeURIComponent(token),
             { method: 'POST', body: JSON.stringify({ passwort }) }),

  /* `nachzueglerGeklaert` IST WEG (Migration 062, 28.08.2026). Ein Nachzügler
     war eine Änderung früher, nachdem der Bereich schon hier lief; es gibt
     keine Quelle mehr, die gelesen wird. Der Weg dahinter ist mitgefallen -
     eine Zeile hier, die auf eine Route zeigt, die es nicht gibt, ist ein
     404 auf Vorrat. */

  // ======================================================== Schreiben
  //
  // Jede aendernde Anfrage schickt den geladenen Stand mit (Feld "stand").
  // Der Server vergleicht ihn mit updated_at und weist ab, wenn in der
  // Zwischenzeit jemand anderes geschrieben hat.

  // --- Auftrag ---
  auftragAnlegen: (b: any) =>
    ruf<any>('/auftraege', { method: 'POST', body: JSON.stringify(b) }),
  auftragAendern: (id: string, b: any) =>
    ruf<any>('/auftraege/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  // `gewerk` ist für den Innendienst freiwillig und für einen Sub Pflicht:
  // er schreibt an SEIN Gewerk, sonst hinge sein Satz an dem einer anderen
  // Firma, mit seinem Namen daneben.
  /** Der Verlauf eines Auftrags: Kommentare, Läufe, Systemereignisse. */
  auftragVerlauf: (id: string, quelle = '') =>
    ruf<any[]>('/auftraege/' + id + '/verlauf'
               + (quelle ? '?quelle=' + encodeURIComponent(quelle) : '')),
  auftragKommentar: (id: string, text: string, gewerk?: string) =>
    ruf<any>('/auftraege/' + id + '/kommentar',
             { method: 'POST', body: JSON.stringify({ text, gewerk }) }),
  /* .498: Textvorlagen für die Auftragsinfo. */
  auftragsinfoVorlagen: (alle = false) => ruf<any[]>('/auftragsinfo/vorlagen' + (alle ? '?alle=ja' : '')),
  auftragsinfoVorlageAnlegen: (b: { titel: string; text: string; reihenfolge?: number }) => ruf<any>('/auftragsinfo/vorlagen', { method: 'POST', body: JSON.stringify(b) }),
  auftragsinfoVorlageAendern: (id: string, b: any) => ruf<any>('/auftragsinfo/vorlagen/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  auftragsinfoVorlageWeg: (id: string) => ruf<any>('/auftragsinfo/vorlagen/' + id, { method: 'DELETE' }),
  /* EINEN EINTRAG DER AUFTRAGSINFO BERICHTIGEN.
     Marvin, 31.08.2026: „bei der sauftragsinfo kann ich nur einen eintrag
     schreiben, aber nichts editieren." Der Server lässt nur Einträge von
     MENSCHEN zu; eine Systemmeldung beschreibt, was geschehen ist. */
  auftragInfoAendern: (id: string, eid: string, text: string) =>
    ruf<any>('/auftraege/' + id + '/info/' + eid,
             { method: 'PATCH', body: JSON.stringify({ text }) }),
  /* UND IHN LÖSCHEN.
     Marvin, 01.09.2026: „jeder seinen eigenen eintrag zu löschen und der
     innendienst alle". Dieselbe Grenze wie beim Berichtigen - wer einen Satz
     ersetzen darf, darf ihn auch entfernen. Die ZEILE bleibt stehen, mit
     Datum und Namen daran. */
  auftragInfoLoeschen: (id: string, eid: string) =>
    ruf<any>('/auftraege/' + id + '/info/' + eid, { method: 'DELETE' }),
  auftragArchivieren: (id: string, grund: string) =>
    ruf<any>('/auftraege/' + id + '/archivieren',
             { method: 'POST', body: JSON.stringify({ grund }) }),
  adresseAendern: (id: string, b: any) =>
    ruf<any>('/adressen/' + id, { method: 'PATCH', body: JSON.stringify(b) }),

  /* AUFTRÄGE WIRKLICH LÖSCHEN, EINZELN UND IM BÜNDEL.
     Marvin, 03.09.2026: „wenn ich sie lösche, sollen auch die daten aus der
     der datenbank verschwinden. auch das sammellöschen brauche ich."
     DIE PROBE STEHT DAVOR: sie sagt VOR dem Löschen, was daran hängt.
     `auftragAnlegen` steht weiter oben - den Weg gab es längst, er ist nur
     nie gegangen worden. */
  auftraegeLoeschenProbe: (ids: string[]) =>
    ruf<any>('/auftraege/loeschen/probe',
             { method: 'POST', body: JSON.stringify({ ids }) }),
  auftraegeLoeschen: (ids: string[], grund: string) =>
    ruf<any>('/auftraege/loeschen',
             { method: 'POST', body: JSON.stringify({ ids, grund }) }),

  // --- Termine ---
  terminpruefung: (id: string, p: URLSearchParams) =>
    ruf<any>('/auftraege/' + id + '/terminpruefung?' + p.toString()),
  /* ERST FRAGEN, DANN PUSHEN (.358): eine Verschiebung geht nicht mehr von
     selbst an die Firma, sondern in die Nachfrage „Firma informieren?". */
  terminBuchen: async (id: string, b: any) => montageFrage(await
    ruf<any>('/auftraege/' + id + '/termin',
             { method: 'POST', body: JSON.stringify({ telegram_von_selbst: false, ...b }) })),
  /* EINE FAHRT IST EINE ANFRAGE UND EINE NACHRICHT.
     ORIGINALTEXT MARVIN (22.09.2026): „überall wo einblasen und spleißen auf
     den gleichen termin liegen schickst du eine gesammelte meldung raus!"
     Wer die Karte des Terminplaners storniert, gibt hier ALLE ihre Gewerke
     mit. Der Server bündelt die Absage; im Browser zu bündeln wäre die 2.
     Fassung derselben Regel. */
  terminStorno: async (gewerkId: string, grund: string, weitere: string[] = [], telegram = false, gbgs: any = null) => montageFrage(await
    ruf<any>('/termine/' + gewerkId + '/storno',
             { method: 'POST',
               body: JSON.stringify({ grund, gewerke: weitere, telegram, ...(gbgs ? { gbgs } : {}) }) })),
  /* Für das Stornofenster (.459): ist der Auftrag im GBGS, welche Gründe, welche Vorlagen. */
  gbgsStornoInfo: (auftragId: string) => ruf<any>(`/auftraege/${auftragId}/gbgs-storno`),
  terminErledigt: (gewerkId: string, b: any) =>
    ruf<any>('/termine/' + gewerkId + '/erledigt',
             { method: 'POST', body: JSON.stringify(b) }),

  // --- Wiedervorlagen ---
  erinnerungAnlegen: (auftragId: string, b: any) =>
    ruf<any>('/auftraege/' + auftragId + '/erinnerungen',
             { method: 'POST', body: JSON.stringify(b) }),
  erinnerungAbhaken: (id: string, erledigt: boolean) =>
    ruf<any>('/erinnerungen/' + id,
             { method: 'PATCH', body: JSON.stringify({ erledigt }) }),

  // --- Aufmass ---
  aufmass: (id: string) => ruf<any>('/auftraege/' + id + '/aufmass'),
  aufmassVorschlag: (id: string, art: string) =>
    ruf<any>('/auftraege/' + id + '/aufmass/vorschlag?art=' + art),
  aufmassVorschlagNehmen: (id: string, positionen: any[]) =>
    ruf<any>('/auftraege/' + id + '/aufmass/vorschlag',
             { method: 'POST', body: JSON.stringify({ positionen }) }),
  aufmassPosition: (id: string, b: any) =>
    ruf<any>('/auftraege/' + id + '/aufmass/position',
             { method: 'POST', body: JSON.stringify(b) }),
  aufmassAendern: (posId: string, b: any) =>
    ruf<any>('/aufmass/' + posId, { method: 'PATCH', body: JSON.stringify(b) }),
  aufmassLoeschen: (posId: string, grund: string) =>
    ruf<any>('/aufmass/' + posId + '?grund=' + encodeURIComponent(grund),
             { method: 'DELETE' }),
  bauabschnitt: (id: string, b: any) =>
    ruf<any>('/auftraege/' + id + '/bauabschnitt',
             { method: 'POST', body: JSON.stringify(b) }),
  bauabschnittLoeschen: (abId: string) =>
    ruf<any>('/bauabschnitt/' + abId, { method: 'DELETE' }),
  aufmassVerschieben: (id: string, b: any) =>
    ruf<any>('/auftraege/' + id + '/aufmass/verschieben',
             { method: 'POST', body: JSON.stringify(b) }),
  aufmassAbsenden: (id: string, begruendung?: string) =>
    ruf<any>('/auftraege/' + id + '/aufmass/absenden',
             { method: 'POST', body: JSON.stringify({ begruendung }) }),
  // WIEDER FREIGEBEN GEHT ÜBER `dokuSchritt` und hat hier keinen eigenen
  // Aufruf mehr (08.09.2026). Zwei Wege über dieselbe Frage laufen beim
  // ersten Nachtrag auseinander - und dieser hier war der ärmere: er
  // schrieb sein Ereignis ohne `sichtbar_fuer_sub` und meldete der Firma
  // nichts. Toten Code entfernen, nicht auskommentieren.
  /* ZWEI ADRESSEN UND KEIN ABRUF.
     ------------------------------------------------------------------------
     Eine Datei wird HERUNTERGELADEN, und das kann der Browser selbst: ein
     `<a href>` nimmt die Keksdose mit, zeigt den Fortschritt und legt die
     Datei dorthin, wohin der Mensch seine Dateien legt. Wer sie stattdessen
     mit `fetch` holt, hält eine ganze PDF im Speicher und muss den Dateinamen
     aus dem Kopf der Antwort wieder auspacken - denselben Namen, den der
     Server ohnehin schon geschickt hat.

     DASS DER CSV-AUSZUG HIER ERST SEIT DEM 09.09.2026 STEHT, IST DER
     EIGENTLICHE BEFUND. Es gab ihn seit dem 24.08.2026, mit Recht, Regel und
     Prüfstand - und KEINE ZEILE der Oberfläche hat ihn je aufgerufen.
     **Ein Weg, den der Server kann und keine Oberfläche geht, ist von einem
     nicht gebauten nicht zu unterscheiden.** */
  aufmassBlattAdresse: (id: string) =>
    '/api/auftraege/' + id + '/aufmass/blatt',
  aufmassAuszugAdresse: (id: string) =>
    '/api/auftraege/' + id + '/aufmass/auszug',

  // --- Dateien ---
  dateien: (id: string) => ruf<any>('/auftraege/' + id + '/dateien'),
  dateiarten: () => ruf<any[]>('/dateiarten'),
  dateiPapierkorb: (id: string) =>
    ruf<{ zeilen: any[]; tage: number }>(
      '/auftraege/' + id + '/dateien/papierkorb'),
  /* DAS FIRMENZEICHEN. Es liegt in derselben Ablage wie jede andere Datei;
     der Leseweg steht ohne Anmeldung offen, weil die Anmeldeseite es zeigt. */
  markeLogoAdresse: () => '/api/marke/logo',
  async markeLogoHochladen(datei: File) {
    const f = new FormData();
    f.append('datei', datei, datei.name);
    const a = await fetch('/api/marke/logo',
                          { method: 'POST', credentials: 'same-origin', body: f });
    const text = await a.text();
    const daten = text ? JSON.parse(text) : null;
    if (!a.ok) throw Object.assign(new Error(daten?.fehler ?? a.statusText),
                                   { daten });
    melde('/marke/logo', { method: 'POST' });
    return daten;
  },
  markeLogoWeg: () => ruf<any>('/marke/logo', { method: 'DELETE' }),

  /* DAS PROFILBILD. Gesetzt wird nur das eigene; entfernen darf auch, wer
     Zugänge verwaltet. Die Adresse trägt einen Stempel: ohne ihn zeigt der
     Browser nach dem Hochladen eine Stunde lang das alte Bild, und wer es
     gerade ausgetauscht hat, sucht den Fehler an der falschen Stelle. */
  profilbildAdresse: (benutzerId: string, stempel?: string | number) =>
    '/api/benutzer/' + benutzerId + '/bild'
    + (stempel ? '?v=' + encodeURIComponent(String(stempel)) : ''),
  async profilbildHochladen(datei: File) {
    const f = new FormData();
    f.append('datei', datei, datei.name);
    const a = await fetch('/api/ich/bild',
                          { method: 'POST', credentials: 'same-origin', body: f });
    const text = await a.text();
    const daten = text ? JSON.parse(text) : null;
    if (!a.ok) throw Object.assign(new Error(daten?.fehler ?? a.statusText),
                                   { daten });
    melde('/ich/bild', { method: 'POST' });
    return daten;
  },
  profilbildWeg: () => ruf<any>('/ich/bild', { method: 'DELETE' }),
  profilbildFremdWeg: (benutzerId: string) =>
    ruf<any>('/benutzer/' + benutzerId + '/bild', { method: 'DELETE' }),
  dateiAendern: (bezugId: string, b: any) =>
    ruf<any>('/dateien/' + bezugId, { method: 'PATCH', body: JSON.stringify(b) }),
  dateiEntfernen: (bezugId: string, grund: string) =>
    ruf<any>('/dateien/' + bezugId + '?grund=' + encodeURIComponent(grund),
             { method: 'DELETE' }),
  dateiZurueckholen: (bezugId: string) =>
    ruf<any>('/dateien/' + bezugId + '/zurueckholen', { method: 'POST' }),
  ablage: () => ruf<any>('/ablage'),
  /**
   * WIE WEIT DIE VORBEREITUNG IM GANZEN BESTAND IST.
   *
   * ORIGINALTEXT MARVIN (09.09.2026)
   *   „ich will, dass das system ueberall automaisch scannt und aktualisiert
   *    ... dass diese fuer alle auftraege gelten und nicht erst ab den neuen."
   *
   * Der Weg rechnet ueber JEDEN Auftrag - er dauert Sekunden und wird deshalb
   * auf Knopfdruck geholt und nicht beim Oeffnen der Seite.
   */
  vorbereitungslage: (grund = '') => ruf<{
    zahlen: {
      auftraege: number; gruen: number; nicht_oeffentlich: number;
      pflicht_fehlt: number; lageplan_fehlt: number; gemeinde_fehlt: number;
      ohne_umgebungsbilder: number;
      felder: { feld: string; n: number }[];
      ohne_person: string[];
    };
    grund: string | null;
    lagen: {
      auftragId: string; kls_id: string | null; sm_nummer: string | null;
      anschrift: string; ort: string | null; grund: string;
      fehlt: string[]; lageplanDa: boolean; umgebungsbilder: number;
      stand: { farbe: string; text: string };
    }[];
  }>('/vorbereitung/lage' + (grund ? '?grund=' + encodeURIComponent(grund) : '')),
  /**
   * DIE ABLAGE WIRKLICH NACHSEHEN, Datei fuer Datei auf der Platte.
   *
   * `gegenrechnen` liest jede Datei GANZ und rechnet ihre Pruefsumme neu -
   * das findet die beschaedigte Datei, die daliegt und sich oeffnen laesst,
   * und es dauert bei einem vollen Bestand Minuten. Der Bildschirm sagt es.
   */
  ablagePruefen: (anwenden: boolean, gegenrechnen = false) => ruf<{
    anwenden: boolean; gegenrechnen: boolean; angesehen: number;
    gekuerzt: boolean; hoechstens: number; geradegezogen: number;
    ohne_auftrag: number; klartext: string;
    lagen: { lage: string; anzahl: number; satz: string; tun: string }[];
    baustellen: { auftrag_id: string; ordner: string; dateien: number;
                  namen: string[] }[];
  }>('/ablage/pruefen',
     { method: 'POST', body: JSON.stringify({ anwenden, gegenrechnen }) }),
  /**
   * Hochladen laeuft ueber FormData, nicht ueber JSON.
   *
   * DIE ART IST FREIWILLIG. Fehlt sie, bestimmt der Server sie je Datei aus
   * Name, Ordner und Dateityp (`artBestimmen` in `regeln/dateiart.ts`). Marvin,
   * 26.08.2026: „ich muss die art des dokuments ja nicht angeben."
   *
   * `undefined` heisst: das Feld geht GAR NICHT mit. Ein leerer String wäre
   * etwas anderes - den würde der Server als „keine gültige Art" abweisen.
   */
  async dateienHochladen(id: string, dateien: FileList | File[],
                         ordner: string, art?: string,
                         sichtbarFuerSub?: boolean,
                         /** Die Ortsfelder aus `teile/standort.ts`, wenn das
                          *  Foto aus dem Kameraknopf kommt. */
                         ort?: Record<string, string>) {
    const f = new FormData();
    f.append('ordner', ordner);
    if (art !== undefined) f.append('art', art);
    if (sichtbarFuerSub !== undefined) {
      f.append('sichtbar_fuer_sub', sichtbarFuerSub ? 'ja' : 'nein');
    }
    /* DIE ORTSFELDER STEHEN VOR DER DATEI, UND DAS IST KEINE SCHÖNHEIT.
       Der Server liest das Formular STÜCKWEISE (`req.parts()`) und stempelt
       jedes Bild, sobald es durch ist. Was nach der Datei käme, wäre beim
       Stempeln noch nicht da - und das Foto ginge ohne Ortsangabe durch,
       ohne dass es jemandem auffiele. */
    for (const [k, w] of Object.entries(ort ?? {})) f.append(k, w);
    for (const d of Array.from(dateien)) f.append('datei', d);
    const a = await fetch('/api/auftraege/' + id + '/dateien',
                          { method: 'POST', credentials: 'same-origin', body: f });
    const text = await a.text();
    const daten = text ? JSON.parse(text) : null;
    if (!a.ok) {
      throw Object.assign(new Error(daten?.fehler ?? a.statusText),
                          { daten, hinweis: daten?.hinweis ?? null });
    }
    melde('/auftraege/' + id + '/dateien', { method: 'POST' });
    return daten;
  },
  /** Ein Paket eines abgeworfenen Ordnerbaums, auf alle Aufträge verteilt. */
  /** Die Datei eines Eingangs (.294): ansehen, oder mit `herunterladen` als Download. */
  eingangDateiAdresse: (id: string, dateiname?: string, herunterladen = false) =>
    '/api/eingang/' + id + '/datei'
    + (dateiname ? '/name/' + encodeURIComponent(dateiname) : '')
    + (herunterladen ? '?herunterladen=1' : ''),
  /* .523: das LEB-Verzeichnis (nur Administration). */
  lebVerzeichnis: (p: URLSearchParams) => ruf<any>('/leb-verzeichnis' + (p.toString() ? '?' + p.toString() : '')),
  dateiAdresse: (bezugId: string, dateiname?: string) =>
    '/api/dateien/' + bezugId
    + (dateiname ? '/name/' + encodeURIComponent(dateiname) : ''),
  /* DIE EIGENE SEITENVORSCHAU (Bild 5, 15.09.2026).
     Der unscharfe Streifen links gehört dem Betrachter des Browsers; wir
     zeichnen ihn selbst, und der Server rendert jede Seite in GENAU der
     Punktzahl, in der sie steht. Begründung in `api/src/seitenbild.ts`. */
  dateiSeiten: (bezugId: string) =>
    ruf<{ seiten: number; pdf: boolean; werkzeug: boolean; grund?: string }>(
      '/dateien/' + bezugId + '/seiten'),
  dateiSeiteAdresse: (bezugId: string, nr: number, breite: number) =>
    '/api/dateien/' + bezugId + '/seite/' + nr + '?breite=' + Math.round(breite),
  /* `dateienSuchen` UND `dateiOrdner` STANDEN HIER, die Suche über ALLE
     Aufträge. Marvin am 27.08.2026: „datei suchen ist eine globale suche
     richtig? die kannst du komplett ausbauen für alle. wir arbeiten nur mit
     den aufträgen." Weg mit Migration 052, samt Recht, Modul und Seite. */

  // --- Die Glocke: was die Automatik gemeldet hat ---
  /* GELESENES KOMMT NICHT MEHR MIT.
     Marvin, 27.08.2026: „wenn einträge auf der notification als gelsen
     markiert wurden, dann soll der eintrag verschwinden." Der Server lässt
     Abgehaktes seit Migration 054 weg; wer es sehen will, fragt die Ecke
     „gelesen". Der frühere Zusatz `?offen=ja` ist damit überflüssig - die
     Glocke hat ihn ohnehin nie gesetzt. */
  /* DIE GLOCKE JE BEREICH (.364): jeder Weg trägt den gewählten Bereich
     mit (App merkt ihn unter mds.bereich), der Server wählt danach die
     Sicht. Marvin, 14.10.2026: „nicht, dass das da durchmischt wird." */
  meldungen: (art = '') => {
    const p = new URLSearchParams();
    if (art) p.set('art', art);
    p.set('bereich', bereichJetzt());
    return ruf<any>('/meldungen?' + p.toString());
  },
  meldungZahl: () =>
    ruf<{ offen: number; dringend: number }>('/meldungen/zahl?bereich=' + bereichJetzt()),
  meldungGelesen: (id: string, art: string, gelesen = true) =>
    ruf<any>('/meldungen/' + id + '/gelesen',
             { method: 'POST', body: JSON.stringify({ art, gelesen, bereich: bereichJetzt() }) }),
  meldungAlleGelesen: (bis?: string, art?: string) =>
    ruf<any>('/meldungen/alle-gelesen',
             { method: 'POST', body: JSON.stringify({ bis, art, bereich: bereichJetzt() }) }),

  /* --- Das Änderungsverzeichnis ---
     Marvin, 02.09.2026: „baue außerdem eine changelog seite, wo du
     festhälfst was wir gemacht haben, damit ich ggf. zu einem
     changelogpunkt zurückspringen kann". */
  changelog: () => ruf<any>('/changelog'),
  changelogNacharbeit: (punkt: string, b: any) =>
    ruf<any>(`/changelog/${encodeURIComponent(punkt)}/nacharbeit`,
             { method: 'POST', body: JSON.stringify(b) }),

  // --- Behoerdenmappe ---
  dokumente: (id: string) => ruf<any>('/auftraege/' + id + '/dokumente'),
  /* ANORDNUNG OHNE ZEITRAUM PRÜFEN (.245): Zeitraum erfassen oder abgelaufen. */
  anordnungPruefen: (id: string, b: { dokument_id?: string | null; bezug_id?: string | null;
                                      gueltig_von?: string; gueltig_bis?: string; abgelaufen?: boolean }) =>
    ruf<any>('/auftraege/' + id + '/anordnung/pruefen', { method: 'POST', body: JSON.stringify(b) }),
  /* WELCHE FELDER DIESES PAPIER BRAUCHT.
     Marvin, 02.09.2026: „wir brauchen bei allen 4 dokumenarten und dann auch
     bei den verschiedenen VAOs alle felder zu den platzhaltern, so wie vorher
     auch." Die Liste kommt vom SERVER: sie hängt am Katalog und am Vordruck
     der Gemeinde, und eine Abschrift im Browser liefe beim ersten neuen
     Vordruck auseinander. */
  dokumentFelder: (id: string, art: string) =>
    ruf<any>(`/auftraege/${id}/dokumente/felder?art=${encodeURIComponent(art)}`),
  /* Der Quicklink (.332): Kennung (KLS, SM, MDS-Nummer) zum Auftrag. */
  auftragUeberKennung: (wert: string) => ruf<any>(`/auftraege/kennung/${encodeURIComponent(wert)}`),
  /* Mail aus der Akte mit Dateien aus dem Ordner (.332). */
  akteMail: (id: string) => ruf<any>(`/auftraege/${id}/mail`),
  akteMailSenden: (id: string, b: { an: string; cc?: string; betreff: string; text: string; dateien: string[] }) =>
    ruf<any>(`/auftraege/${id}/mail`, { method: 'POST', body: JSON.stringify(b) }),
  /* Die Sicherung (.332). */
  sicherungen: () => ruf<any>('/sicherung'),
  sicherungJetzt: () => ruf<any>('/sicherung/jetzt', { method: 'POST', body: '{}' }),
  /* .616: Update aus der Plattform einspielen. */
  /* .621: Version von GitHub einspielen (Commit, den die Karte zeigt). */
  updateGithubEinspielen: (commit: string) => ruf<any>('/update/github-einspielen', { method: 'POST', body: JSON.stringify({ commit }) }),
  updateStand: () => ruf<any>('/update/stand'),
  updateHochladen: (datei: File) => sendeDateiMitFeldern<any>('/update/hochladen', datei, {}),
  dokumentErzeugen: (id: string, b: any) =>
    ruf<any>('/auftraege/' + id + '/dokumente/erzeugen',
             { method: 'POST', body: JSON.stringify(b) }),
  // ANSEHEN, BEVOR ES IN DEN ORDNER GEHT. Der Weg gab es seit dem 10.08.2026
  // auf dem Server und wurde von keiner Zeile der Oberfläche gerufen: man
  // konnte ein Behördenpapier nur erzeugen und danach lesen, und ein falsches
  // Exemplar blieb als „ersetzt" stehen. Die Vorschau legt NICHTS ab.
  dokumentVorschau: (id: string, b: any) =>
    rufRoh('/auftraege/' + id + '/dokumente/vorschau',
           { method: 'POST', body: JSON.stringify(b) }),
  dokumentEingang: (id: string, b: any) =>
    ruf<any>('/auftraege/' + id + '/dokumente/eingang',
             { method: 'POST', body: JSON.stringify(b) }),
  /* EINE DATEI AN EIN BEHÖRDENPAPIER HÄNGEN - ODER SIE LÖSEN.
     Marvin, 31.08.2026: „Ich muss die Möglichkeit haben, wenn hier Dateien
     vorhanden sind in der Behördenmappe, dass ich die auch anklicken kann."
     `bezug_id: null` löst die Verknüpfung wieder. */
  dokumentDatei: (id: string, bezugId: string | null) =>
    ruf<any>('/dokumente/' + id + '/datei',
             { method: 'PATCH', body: JSON.stringify({ bezug_id: bezugId }) }),
  dateienZurAuswahl: (auftragId: string) =>
    ruf<any[]>('/auftraege/' + auftragId + '/dateien-zur-auswahl'),
  dokumentAendern: (id: string, b: any) =>
    ruf<any>('/dokumente/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  /* ZURÜCKZIEHEN IST KEIN LÖSCHEN. Die Zeile bleibt mit Datum, Person und
     Grund stehen; der Grund ist Pflicht. */
  dokumentZurueckziehen: (id: string, grund: string) =>
    ruf<any>('/dokumente/' + id + '/zurueckziehen',
             { method: 'POST', body: JSON.stringify({ grund }) }),
  dokumentVersenden: (id: string, b: any = {}) =>
    ruf<any>('/dokumente/' + id + '/versenden',
             { method: 'POST', body: JSON.stringify(b) }),
  /* BEHÖRDENPOST: EINE Mail mit allem, was zusammengehört.
     Marvin, 03.09.2026: „ich will ja nicht jedes dokument einzelt versenden
     ... sondern es werden ja die entsprechenden dokumente zusammenversendet
     mit email, anhang usw." Die Vorschau nennt die DATEINAMEN und nicht ihre
     Anzahl - vier Dateien zu senden, von denen eine die falsche ist, fällt
     sonst niemandem auf. */
  behoerdenpostVorschau: (auftragId: string, sorte: string, wahl: any = {}, keinTbOeffentlich?: boolean) =>
    ruf<any>('/auftraege/' + auftragId + '/behoerdenpost/vorschau',
             { method: 'POST', body: JSON.stringify({ sorte, wahl,
               ...(typeof keinTbOeffentlich === 'boolean' ? { kein_tb_oeffentlich: keinTbOeffentlich } : {}) }) }),
  behoerdenpostAnstossen: (auftragId: string) =>
    ruf<any>('/auftraege/' + auftragId + '/behoerdenpost/anstossen', { method: 'POST', body: '{}' }),
  behoerdenpostSenden: (auftragId: string, b: any) =>
    ruf<any>('/auftraege/' + auftragId + '/behoerdenpost',
             { method: 'POST', body: JSON.stringify(b) }),

  /* DIE SCHULUNGSSTUFE VOR DER BEHÖRDENPOST (Migration 126). Freigeben ist
     kein zweiter Versandweg: der Server baut die Sendung neu und schickt sie
     über dieselbe Stelle wie jede andere - wer nach der Rückmeldung ein
     besseres Foto hochgeladen hat, verschickt auch das bessere. */
  freigaben: (auftragId: string) =>
    ruf<any[]>('/auftraege/' + auftragId + '/behoerdenfreigaben'),
  /* DER POSTAUSGANG (.315): jede Behördenmail dieses Auftrags mit Ergebnis. */
  postausgang: (auftragId: string) =>
    ruf<any[]>('/auftraege/' + auftragId + '/behoerdenpost/ausgang'),
  freigabeFreigeben: (id: string, b: any = {}) =>
    ruf<any>('/behoerdenfreigaben/' + id + '/freigeben',
             { method: 'POST', body: JSON.stringify(b) }),
  freigabeSchonVersendet: (id: string) =>
    ruf<any>('/behoerdenfreigaben/' + id + '/schon-versendet', { method: 'POST', body: '{}' }),
  behoerdenpostSchonVersendet: (auftragId: string, sorte: string) =>
    ruf<any>('/auftraege/' + auftragId + '/behoerdenpost/schon-versendet',
             { method: 'POST', body: JSON.stringify({ sorte }) }),
  freigabeAblehnen: (id: string, grund: string, telegram = true) =>
    ruf<any>('/behoerdenfreigaben/' + id + '/ablehnen',
             { method: 'POST', body: JSON.stringify({ grund, telegram }) }),

  /* WAS SICH EIN BILDSCHIRM MERKT. Je Bildschirm ein Schlüssel; was darin
     steht, entscheidet der Bildschirm selbst. Es ist eine ANSICHT und nie ein
     Recht. */
  ansichtMerken: (bildschirm: string, wert: any) =>
    ruf<any>('/ich/ansicht',
             { method: 'PUT',
               body: JSON.stringify({ bildschirm, wert }) }),

  /** Die Terminmails an den Kunden: Vorschau und Versand über EINEN Weg. */
  terminmail: (auftragId: string, b: any) =>
    ruf<any>('/auftraege/' + auftragId + '/terminmail',
             { method: 'POST', body: JSON.stringify(b) }),

  /** „Kunde nicht erreicht" setzen oder zurücknehmen. */
  nachfassen: (auftragId: string, b: any) =>
    ruf<any>('/auftraege/' + auftragId + '/nachfassen',
             { method: 'POST', body: JSON.stringify(b) }),
  dokumentPortal: (id: string, b: any = {}) =>
    ruf<any>('/dokumente/' + id + '/portal',
             { method: 'POST', body: JSON.stringify(b) }),
  nachricht: (id: string) => ruf<any>('/nachrichten/' + id),
  mailProbe: (an?: string) =>
    ruf<any>('/mail/probe', { method: 'POST', body: JSON.stringify({ an }) }),

  // --- Stammdaten ---
  eineGemeinde: (id: string) => ruf<any>('/gemeinden/' + id),
  gemeindeAnlegen: (b: any) =>
    ruf<any>('/gemeinden', { method: 'POST', body: JSON.stringify(b) }),
  gemeindeAendern: (id: string, b: any) =>
    ruf<any>('/gemeinden/' + id, { method: 'PATCH', body: JSON.stringify(b) }),

  // --- Vorlagen: die Vordrucke der Behoerden ---
  vorlagenAlle: () => ruf<any[]>('/vorlagen?alle=1'),
  vorlagen: (art?: string) =>
    ruf<any[]>('/vorlagen' + (art ? '?art=' + encodeURIComponent(art) : '')),
  /**
   * Einen Vordruck hochladen.
   *
   * Die Antwort sagt, welche Platzhalter darin stehen und welche davon
   * dieses System nicht fuellt. Das ist der Punkt des Weges und nicht ein
   * Zusatz: ein Platzhalter, den niemand fuellt, sieht im fertigen Papier
   * aus wie ein leeres Feld im Vordruck.
   */
  async vorlageHochladen(datei: File, art: string, name: string,
                         bemerkung?: string) {
    const f = new FormData();
    f.append('art', art);
    if (name) f.append('name', name);
    if (bemerkung) f.append('bemerkung', bemerkung);
    f.append('datei', datei, datei.name);
    const a = await fetch('/api/vorlagen',
                          { method: 'POST', credentials: 'same-origin', body: f });
    const text = await a.text();
    const daten = text ? JSON.parse(text) : null;
    if (!a.ok) throw Object.assign(new Error(daten?.fehler ?? a.statusText), { daten });
    melde('/vorlagen', { method: 'POST' });
    return daten;
  },
  vorlageAendern: (id: string, b: any) =>
    ruf<any>('/vorlagen/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  vorlageAdresse: (id: string) => '/api/vorlagen/' + id + '/datei',
  /* .221: ANSEHEN ALS PDF, DATEI AUSTAUSCHEN, LÖSCHEN.
     Marvin: „alte dateien lassen sich nicht öffnen, auch nicht löschen". */
  /** Die Adresse eines PDFs zum Ansehen (`rufRoh`, wie jedes andere Papier). */
  vorlageAnsicht: (id: string) => rufRoh('/vorlagen/' + id + '/ansicht'),
  async vorlageDateiTauschen(id: string, datei: File) {
    const f = new FormData();
    f.append('datei', datei, datei.name);
    const a = await fetch('/api/vorlagen/' + id + '/datei',
                          { method: 'PUT', credentials: 'same-origin', body: f });
    const text = await a.text();
    const daten = text ? JSON.parse(text) : null;
    if (!a.ok) throw Object.assign(new Error(daten?.fehler ?? a.statusText), { daten });
    melde('/vorlagen', { method: 'PUT' });
    return daten;
  },
  vorlageLoeschen: (id: string) =>
    ruf<any>('/vorlagen/' + id, { method: 'DELETE' }),
  /* DIE VORDRUCKE AUS DEM ROHBESTAND HOLEN. Erst die Probe, dann der Lauf -
     dieselbe Bauart wie beim Dateiumzug: wer einen Ordnerbaum durchsucht, will
     vorher sehen, was dabei herauskommt. */
  /* Die Mailvorlagen. Sie leben als Konfigurationsschlüssel weiter; dieser
     Bildschirm ist eine andere ANSICHT darauf und keine Kopie. */
  mailvorlagen: () => ruf<any>('/mailvorlagen'),
  mailvorlageSpeichern: (wert: string, b: any) =>
    ruf<any>('/mailvorlagen/' + encodeURIComponent(wert),
             { method: 'PUT', body: JSON.stringify(b) }),
  mailvorschau: (wert: string, b: any) =>
    ruf<any>('/mailvorlagen/' + encodeURIComponent(wert) + '/vorschau',
             { method: 'POST', body: JSON.stringify(b) }),

  /* Welche Behörde welchen Vordruck bekommt. Marvin, 27.08.2026: „was ist mit
     der verknüpfung zu den behörden?" - sie stand in zwei Hälften auf zwei
     Bildschirmen und nirgends zusammen. */
  vorlagenZuordnung: () => ruf<any>('/vorlagen/zuordnung'),
  /* WELCHE MARKE NENNT WELCHER VORDRUCK - und füllt dieses System sie?
     Marvin, 09.09.2026: „prüfe auch alle anderen dokumente!" Bis dahin liess
     sich das nur an einem fertigen Papier ablesen. */
  vorlagenMarken: () => ruf<any>('/vorlagen/marken'),
  links: () => ruf<any[]>('/links?bereich=' + bereichJetzt()),
  linksAlle: () => ruf<any[]>('/links/alle'),
  linkAnlegen: (b: any) =>
    ruf<any>('/links', { method: 'POST', body: JSON.stringify(b) }),
  linkAendern: (id: string, b: any) =>
    ruf<any>('/links/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  linkWeg: (id: string) => ruf<any>('/links/' + id, { method: 'DELETE' }),
  /* DAS ZEICHEN EINES LINKS. Es wird HOCHGELADEN und nicht gezeichnet: eine
     fremde Marke aus dem Gedächtnis nachzubauen ist immer falsch. */
  linkZeichenAdresse: (id: string) => '/api/links/' + id + '/icon',
  async linkZeichenHochladen(id: string, datei: File) {
    const f = new FormData();
    f.append('datei', datei, datei.name);
    const a = await fetch('/api/links/' + id + '/icon',
                          { method: 'POST', credentials: 'same-origin', body: f });
    const text = await a.text();
    const daten = text ? JSON.parse(text) : null;
    if (!a.ok) throw Object.assign(new Error(daten?.fehler ?? a.statusText),
                                   { daten });
    melde('/links/' + id + '/icon', { method: 'POST' });
    return daten;
  },
  linkZeichenWeg: (id: string) =>
    ruf<any>('/links/' + id + '/icon', { method: 'DELETE' }),

  /* Eine Probenachricht an den Innendienstchat. Die Antwort sagt, WAS
     passiert ist - „gesendet", „Testmodus" und „kein Token" sind drei
     verschiedene Auskünfte. */
  telegramProbe: (chat?: string) =>
    ruf<any>('/nachrichten/telegram/probe',
             { method: 'POST', body: JSON.stringify({ chat }) }),

  einSub: (id: string) => ruf<any>('/subunternehmer/' + id),
  subAnlegen: (b: any) =>
    ruf<any>('/subunternehmer', { method: 'POST', body: JSON.stringify(b) }),
  subAendern: (id: string, b: any) =>
    ruf<any>('/subunternehmer/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  lvAlle: () => ruf<any[]>('/lv/alle'),
  lvAnlegen: (b: any) =>
    ruf<any>('/lv', { method: 'POST', body: JSON.stringify(b) }),
  lvAendern: (id: string, b: any) =>
    ruf<any>('/lv/' + id, { method: 'PATCH', body: JSON.stringify(b) }),
  lvAusserKraft: (id: string, ab?: string) =>
    ruf<any>('/lv/' + id + '/ausser-kraft',
             { method: 'POST', body: JSON.stringify({ ab }) }),

  // --- LEB-Export ---
  leb: (auftragId: string) => ruf<any>('/auftraege/' + auftragId + '/leb'),
  lebZeitraum: (auftragId: string, von: string | null, bis: string | null) =>
    ruf<any>('/auftraege/' + auftragId + '/leb/zeitraum', { method: 'PATCH', body: JSON.stringify({ von, bis }) }),
  /* DAS ADRESSBUCH (.358). */
  adressbuch: (bereich: 'ne3' | 'ne4' = 'ne3') => ruf<{ gruppen: any[]; kontakte: any[]; plattform: { schluessel: string; name: string; kontakte: any[] }[]; ausgeblendet: any[] }>('/adressbuch?bereich=' + bereich),
  adressbuchGruppe: (w: { name: string; eltern_id?: string | null; bereich?: 'ne3' | 'ne4' }) =>
    ruf<any>('/adressbuch/gruppen', { method: 'POST', body: JSON.stringify(w) }),
  adressbuchGruppeAendern: (id: string, name: string) =>
    ruf<any>('/adressbuch/gruppen/' + id, { method: 'PATCH', body: JSON.stringify({ name }) }),
  adressbuchGruppeLoeschen: (id: string, mitKontakten = false) =>
    ruf<any>('/adressbuch/gruppen/' + id + (mitKontakten ? '?mit_kontakten=ja' : ''), { method: 'DELETE' }),
  adressbuchKontakt: (w: any) =>
    ruf<any>('/adressbuch/kontakte', { method: 'POST', body: JSON.stringify(w) }),
  adressbuchKontaktAendern: (id: string, w: any) =>
    ruf<any>('/adressbuch/kontakte/' + id, { method: 'PATCH', body: JSON.stringify(w) }),
  adressbuchKontaktLoeschen: (id: string, zurueck = false) =>
    ruf<any>('/adressbuch/kontakte/' + id + (zurueck ? '?zurueck=ja' : ''), { method: 'DELETE' }),
  /* .588: Ansprechpartner unter einem Eintrag. */
  adressbuchPartner: (w: any) => ruf<any>('/adressbuch/ansprechpartner', { method: 'POST', body: JSON.stringify(w) }),
  adressbuchPartnerAendern: (id: string, w: any) => ruf<any>('/adressbuch/ansprechpartner/' + id, { method: 'PATCH', body: JSON.stringify(w) }),
  adressbuchPartnerLoeschen: (id: string) => ruf<any>('/adressbuch/ansprechpartner/' + id, { method: 'DELETE' }),
  papierkorb: (suche?: string) =>
    ruf<{ zeilen: any[]; tage: number }>('/papierkorb' + (suche ? '?suche=' + encodeURIComponent(suche) : '')),
  lebVorschauAdresse: (auftragId: string, art?: string) =>
    '/api/auftraege/' + auftragId + '/leb/vorschau' + (art ? '?art=' + art : ''),
  /* DIE MENGEN, DIE WIRKLICH ABGERECHNET WERDEN. Die Liste ist vollständig:
     was nicht mitkommt, wird zurückgenommen. Sie geht in die DATENBANK und
     nicht als Beigabe an die Vorschau - sonst zeigte die Datei zum
     Herunterladen weiter die gerechneten Zahlen. */
  lebMengen: (auftragId: string, aenderungen: any[]) =>
    ruf<any>('/auftraege/' + auftragId + '/leb/mengen',
             { method: 'PUT', body: JSON.stringify({ aenderungen }) }),
  lebErzeugen: (auftragId: string, b: any) =>
    ruf<any>('/auftraege/' + auftragId + '/leb',
             { method: 'POST', body: JSON.stringify(b) }),
  lebZuruecknehmen: (id: string, grund: string) =>
    ruf<any>('/leb/' + id + '?grund=' + encodeURIComponent(grund),
             { method: 'DELETE' }),
  lebAlle: () => ruf<any[]>('/leb'),

  // --- Konfiguration ---
  evergabePruefen: () => ruf<{ ok: boolean; text: string }>('/konfiguration/evergabe/pruefen', { method: 'POST' }),
  konfigAendern: (schluessel: string, wert: string | null, mirIstKlar?: boolean) =>
    ruf<any>('/konfiguration/' + encodeURIComponent(schluessel),
             { method: 'PATCH',
               body: JSON.stringify({ wert, mir_ist_klar: mirIstKlar }) }),
};

/* HIER STAND `Herkunftsstand`, UND ER BESCHRIEB EINEN ZUSTAND, DEN ES NICHT
 * MEHR GIBT: welche Bereiche haelt noch eine fremde Anwendung, wie viele sind
 * es, sind wir schon autark? Der Umbau ist am 28.08.2026 abgeschlossen, die
 * Antwort lautet seither an jeder Stelle gleich, und ein Typ, dessen Felder
 * nur noch einen Wert annehmen koennen, beschreibt nichts mehr.
 *
 * Gefallen ist er mit Migration 102 samt dem Feld `herkunft` in der Antwort
 * des Bauplans. */

/**
 * Ein Menuepunkt mit seinen Rechten.
 *
 * HIER STANDEN BIS MIGRATION 102 VIER WEITERE FELDER: `stand`, `kann`,
 * `fehlt` und `ersetzt`. Sie waren die Fortschrittsanzeige des Umbaus, und
 * `ersetzt` hielt fest, was an dieser Stelle in der Vorgaengeranwendung
 * stand. Beides ist fort - der Umbau ist abgeschlossen, und diese Plattform
 * fuehrt keine Angaben ueber eine fremde Anwendung mit.
 *
 * Der Name bleibt trotzdem `Bauteil`: er steht an zwei Dutzend Stellen, und
 * eine Umbenennung waere eine Aenderung ohne Wirkung mit Gelegenheit zum
 * Fehler.
 */
export interface Bauteil {
  schluessel: string; name: string; gruppe: string;
  im_menue: boolean; fuer_subs: boolean;
  /** In welchen Bereichen der Bildschirm steht (.333). */
  bereiche?: string[];
  /** Alle Rechte dieses Moduls. */
  rechte: string[];
  /** Die davon, die dieser Benutzer hat. Nie leer: sonst kaeme das Modul nicht. */
  darf: string[];
}


/** Macht aus dem, was die Datenbank liefert, verlaesslich eine Liste.
 *  Ein Array von eigenen Aufzaehlungstypen kommt als "{TB,EB}" an. */
export function liste(w: unknown): string[] {
  if (Array.isArray(w)) return w as string[];
  if (typeof w === 'string' && w.startsWith('{'))
    return w.slice(1, -1).split(',').filter(Boolean);
  return [];
}

/**
 * Eine Rufnummer, wie sie in einen `webextel:`-Verweis gehoert.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „bei aufträgen fehlen die Auftragsinfos und die rufnummern auf der
 *    habtseite."
 *
 * Der Telefonanlagen-Verweis `webextel:` waehlt die Nummer im Tischtelefon.
 * Er vertraegt keine Leerzeichen, Schraegstriche oder Klammern, und aus
 * „0261 / 123 45-6" muss „02611234 56" nicht werden, sondern „02611234 56"
 * ohne Trenner: „0261123456".
 *
 * WAS NICHT PASSIERT: es wird nichts erfunden. Steht in der Spalte ein Text
 * ohne eine einzige Ziffer, kommt leer zurueck und der Verweis entfaellt.
 * Eine Nummer, die beim Klick irgendwo anruft, ist schlimmer als keine.
 */
export function waehlbar(nr: string | null | undefined): string {
  const roh = String(nr ?? '').trim();
  if (!roh) return '';
  const plus = roh.startsWith('+');
  const ziffern = roh.replace(/\D+/g, '');
  if (ziffern.length < 4) return '';
  return (plus ? '+' : '') + ziffern;
}

/**
 * Ein Routenlink zu einer Adresse.
 *
 * ORIGINALTEXT MARVIN (Spalte „Navigation" im Master)
 *
 * Früher stand in Spalte I ein fertiger Maps-Link je Zeile, gebaut beim
 * Import. Hier wird er GERECHNET: ein gespeicherter Link neben einer
 * gespeicherten Adresse sind zwei Wahrheiten, und nach der ersten
 * Adresskorrektur führt er zum alten Haus.
 *
 * WAS NICHT PASSIERT: es wird nichts erfunden. Ohne Straße kommt leer zurück
 * und der Verweis entfällt. Eine Navigation, die den Tiefbauer irgendwohin
 * schickt, ist schlimmer als keine.
 */
export function navUrl(a: {
  strasse?: string | null; hausnummer?: string | null;
  hausnummer_zusatz?: string | null; plz?: string | null;
  ort_text?: string | null;
} | null | undefined): string {
  if (!a?.strasse) return '';
  const ziel = [
    `${a.strasse} ${a.hausnummer ?? ''}${a.hausnummer_zusatz ?? ''}`.trim(),
    `${a.plz ?? ''} ${a.ort_text ?? ''}`.trim(),
  ].filter(Boolean).join(', ');
  return 'https://www.google.com/maps/dir/?api=1&destination='
    + encodeURIComponent(ziel);
}

export function darf(a: Anmelder | null, recht: string) {
  return !!a && a.rechte.includes(recht);
}

// --- Anzeige, deutsch. ISO nur intern. ------------------------------------

/**
 * DIE ZEITZONE DIESES HAUSES.
 *
 * ORIGINALTEXT MARVIN (25.08.2026)
 *   „nicht nur hier sondern generell soll das datumsformat und uhrzeit
 *    europäisch sein. die plattform muss die deutsche zeitzone haben."
 *
 * Sie steht fest und wird NICHT vom Browser übernommen. Ein Termin um 07:30
 * ist ein Termin um 07:30, auch wenn jemand aus dem Urlaub auf Mallorca
 * hineinsieht - es ist die Zeit, zu der der Bagger anrollt, und die richtet
 * sich nicht nach der Uhr des Betrachters.
 */
const ZEITZONE = 'Europe/Berlin';

/**
 * EIN TAG IST KEIN ZEITPUNKT, und er wird deshalb auch nicht wie einer
 * behandelt.
 *
 * Hier stand `new Date(w).toLocaleDateString(...)`. Bei einem blanken
 * „2026-08-31" liest JavaScript UTC-Mitternacht und rechnet danach in die
 * Zeitzone des BROWSERS um. In Berlin geht das gut, westlich von Greenwich
 * steht dann der Vortag da - und niemand hier hätte es je gesehen.
 *
 * Ein Datum ohne Uhrzeit hat keine Zeitzone. Es wird deshalb einfach
 * umgestellt, ohne Umweg über eine Uhr.
 *
 * Die zweite Fassung (mit Uhrzeit darin) ist ein echter Zeitpunkt und wird
 * sehr wohl umgerechnet - nach `ZEITZONE`, nicht nach der des Betrachters.
 */
function tagTeile(w: string): [string, string, string] | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(w.trim());
  return m ? [m[1], m[2], m[3]] : null;
}

/**
 * WELCHER TAG IST HEUTE - in DEUTSCHER Zeit, nicht in der des Browsers.
 *
 * ORIGINALTEXT MARVIN (09.09.2026)
 *   „Also grundsätzlich und überall auf dem server läuft dieser in deutscher
 *    zeit. behebe alle fehler dazu in der plattform"
 *
 * SIE STAND SECHSMAL VON HAND DA, jedes Mal als eigenes `iso()` oder `tagIso()`
 * (`teile/Wochenraster.tsx`, `teile/Betrieb.tsx`, `seiten/Terminplaner.tsx`,
 * `seiten/Verfuegbarkeit.tsx`, `seiten/Zahlen.tsx` zweimal), und jedes Mal über
 * `getFullYear/getMonth/getDate` - also über die Uhr des BETRACHTERS. Daneben
 * lagen neun `heuteIso()`, die in UTC rechnen.
 *
 * **DIE ANZEIGE STAND DAMIT AUF BERLIN UND DIE RECHNUNG AUF DEM BROWSER**, und
 * beide sagten in einer anderen Zone einen anderen Tag. `Terminplaner.tsx`
 * verbietet `toISOString()` seit dem 27.08.2026 in einem eigenen Kommentar -
 * und ruft es acht Zeilen weiter selbst auf.
 *
 * Sie steht ab dem 09.09.2026 hier, EINMAL, und trägt die Zone selbst.
 */
export function heuteIso(jetzt: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ZEITZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(jetzt);
}

/**
 * Ein beliebiger Zeitpunkt als Tag in deutscher Zeit, JJJJ-MM-TT.
 *
 * Für alles, was aus einem `Date` einen TAG macht - Kalenderraster,
 * Wochenrechnung, „ist das heute". Ein blanker Tag bleibt unangetastet: er hat
 * keine Zeitzone, und ihn durch eine Uhr zu schicken ist genau der Fehler, vor
 * dem `tagTeile` warnt.
 */
export function tagIso(w: string | Date | null | undefined): string {
  if (!w) return '';
  if (typeof w === 'string') {
    const t = tagTeile(w);
    if (t) return `${t[0]}-${t[1]}-${t[2]}`;
    const d = new Date(w);
    return Number.isNaN(d.getTime()) ? '' : heuteIso(d);
  }
  return Number.isNaN(w.getTime()) ? '' : heuteIso(w);
}

/**
 * DIE BAUSTELLE, WIE SIE ÜBER EINEM FENSTER STEHT.
 * ============================================================================
 * ORIGINALTEXT MARVIN (03.09.2026)
 *   „ein weiterer punkt und der ist überall bei den aufträgen mit dem popup:
 *    da steht bisher nur die Straße, hausnummer, ich will die komplette
 *    adresse stehen haben, auch mit KLS und SM. hintergrund ist das ich nicht
 *    nochmal abrechen muss und ich habe eh platz im titel."
 *
 * **DER SATZ STAND ACHTMAL IM HAUS**, jedes Mal von Hand
 * (`${strasse} ${hausnummer}${zusatz}`) - sechsmal allein in der Auftragsakte.
 * Acht Abschriften heissen, dass die neunte etwas anderes zeigt; und weil
 * jede für sich klein aussah, hat auch niemand gemerkt, dass in ALLEN die
 * Postleitzahl fehlte. Es gibt sie jetzt einmal.
 *
 * DIE KENNUNGEN GEHÖREN DAZU UND NICHT AN DEN ANFANG. Man sucht das Fenster
 * über die Anschrift und PRÜFT über die Nummer - „Beispielweg 4A, 56410
 * Musterstadt, KLS 11432223, SM 212703111" liest sich in dieser Reihenfolge.
 * Was fehlt, steht nicht da: „KLS -" wäre eine Zeile über eine Lücke.
 */
export function baustelle(a: {
  strasse?: string | null; hausnummer?: string | null;
  hausnummer_zusatz?: string | null; plz?: string | null;
  ort_text?: string | null; kls_id?: string | null; sm_nummer?: string | null;
} | null | undefined): string {
  if (!a) return '';
  const anschrift = [
    `${a.strasse ?? ''} ${a.hausnummer ?? ''}${a.hausnummer_zusatz ?? ''}`.trim(),
    [a.plz, a.ort_text].filter(Boolean).join(' ').trim(),
  ].filter(Boolean).join(', ');
  return [anschrift,
          a.kls_id ? `KLS ${a.kls_id}` : '',
          a.sm_nummer ? `SM ${a.sm_nummer}` : '']
    .filter(Boolean).join(', ');
}

/**
 * EIN GELESENER WERT, WIE MAN IHN LIEST (.284).
 * ORIGINALTEXT MARVIN (02.10.2026): „der scanner hat das datum geesen, aber
 * es wird nicht euröpäisch angezeigt."
 * Intern steht ein Tag als 2026-09-18 (so rechnet die Datenbank); auf dem
 * Bildschirm steht er als 18.09.2026 - auch mitten in einem Wert wie
 * „2026-09-18 bis 2026-10-09".
 */
export function wertDeutsch(w: unknown): string {
  return String(w ?? '').replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, '$3.$2.$1');
}

export function datum(w: string | Date | null | undefined): string {
  if (!w) return '';
  /* EIN `Date` KOMMT HIER AN, SEIT DIE ELF `toLocaleDateString`-STELLEN
     hierher gezogen sind (09.09.2026). `String(einDate)` ist „Tue Sep 09
     2026 …", darin steht kein Bindestrich, `tagTeile` findet nichts, und
     der Zweig darunter faengt es richtig auf. */
  const t = typeof w === 'string' ? tagTeile(w) : null;
  if (t) return `${t[2]}.${t[1]}.${t[0]}`;
  const d = new Date(w);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: ZEITZONE,
  });
}

export function datumKurz(w: string | null | undefined): string {
  if (!w) return '';
  const t = tagTeile(String(w));
  if (t) return `${t[2]}.${t[1]}.`;
  const d = new Date(w);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-DE',
    { day: '2-digit', month: '2-digit', timeZone: ZEITZONE });
}

/**
 * Ein Monat als „Oktober 2025" statt als „2025-10".
 *
 * Marvin, 25.08.2026, zur Auswertung: „generell soll das datumsformat und
 * uhrzeit europäisch sein." In der Spalte „Monat" stand die Sortierform der
 * Datenbank. Sie ist zum Sortieren gemacht, nicht zum Lesen.
 */
const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli',
                'August', 'September', 'Oktober', 'November', 'Dezember'];

export function monat(w: string | null | undefined): string {
  if (!w) return '';
  const m = /^(\d{4})-(\d{2})/.exec(String(w).trim());
  if (!m) return String(w);
  const nr = Number(m[2]);
  return nr >= 1 && nr <= 12 ? `${MONATE[nr - 1]} ${m[1]}` : String(w);
}

/**
 * Ein Zeitpunkt als „27.08.2026 14:39".
 *
 * VIER STELLEN FÜR DAS JAHR, und das ist keine Geschmacksfrage.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „da steht das datum nicht europäisch drin. bitte ändern merke dir das für
 *    immer."
 *
 * Hier stand bis zum 27.08.2026 `year: '2-digit'`, und auf dem Bildschirm
 * „Benutzer und Rechte" las man in der Spalte „Anmeldung" ein „27.08.26".
 * Der Server schreibt an derselben Stelle vier Stellen (`regeln/zeit.ts`,
 * Prüfstand 021 misst es) - zwei Schreibweisen für dieselbe Sache, und die
 * kürzere ist die schlechtere: „26" ist 1926 oder 2026, je nachdem, wer
 * hinsieht. Gefunden hat es kein Prüfstand, sondern ein Bildschirmfoto des
 * Handbuchs.
 */
export function zeitpunkt(w: string | Date | null | undefined): string {
  if (!w) return '';
  // Ein blanker Tag hat keine Uhrzeit, die man dazuerfinden dürfte.
  const t = typeof w === 'string' ? tagTeile(w) : null;
  if (t) return `${t[2]}.${t[1]}.${t[0]}`;
  const d = new Date(w);
  if (Number.isNaN(d.getTime())) return '';
  return (
    d.toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', timeZone: ZEITZONE,
    }) + ' ' +
    d.toLocaleTimeString('de-DE',
      { hour: '2-digit', minute: '2-digit', timeZone: ZEITZONE })
  );
}

/**
 * Die Kalenderwoche nach ISO 8601. Der Donnerstag entscheidet, welches Jahr.
 *
 * SIE STAND DREIMAL IM HAUS, und die drei Fassungen rechneten verschieden:
 * `teile/Wochenraster.tsx` über Ortszeit, `seiten/Verfuegbarkeit.tsx` über
 * UTC, und die Auswertung gar nicht. **Wer ab dem 1. Januar zählt, bekommt
 * jedes zweite Jahr eine „KW 53", die es nicht gibt** - und zwei Bildschirme,
 * die verschieden zählen, sind an genau einem Tag im Jahr uneins, und dann
 * glaubt niemand mehr einem von beiden.
 */
export function kalenderwoche(d: Date | string): number {
  /* SIE RECHNETE UEBER `Date` UND DIE LOKALEN GETTER - also auf der Uhr des
     BETRACHTERS.
     ========================================================================
     ORIGINALTEXT MARVIN (10.09.2026, mit 2 Bildschirmfotos)
       „bild 1: von kamil, deutsche zeitzone, bild 2: mein pc, zypern
        zeitzone. aber das problem sind die wochentage, datum."

     Mitternacht am 07.09. in Zypern ist in Berlin der 06.09. um 23:00 - und
     eine Woche, die einen Tag frueher anfaengt, ist am Sonntag die falsche.
     Gerechnet wird jetzt ohne jede Uhr, in `teile/rastertage.ts`; gemessen
     wird es in Pruefstand 022, der ausdruecklich in Marvins Zone laeuft. */
  const t = typeof d === 'string' ? tagIso(d) : tagIso(d);
  return t ? kwVon(t) : 0;
}

/** Das Jahr, zu dem die Kalenderwoche zählt - am Jahreswechsel nicht das des
 *  Tages. Der 31.12.2025 liegt in der KW 1 von 2026. */
export function kwJahr(d: Date | string): number {
  const t = tagIso(d);
  return t ? kwJahrVon(t) : 0;
}

/**
 * Eine Kalenderwoche, wie ein Mensch sie liest: „KW 36/2026 (31.08.-06.09.)".
 *
 * EINE KALENDERWOCHE IMMER MIT ZEITRAUM. „KW 36" allein zwingt jeden dazu,
 * in einen Kalender zu sehen, und im Innendienst sitzen mehrere Leute mit
 * verschiedenen Kalendern. Fällt der Zeitraum nicht auf eine ganze Woche,
 * gibt die Funktion `null` zurück - eine behauptete Woche über einem Zeitraum,
 * der keine ist, wäre schlimmer als keine Angabe.
 */
export function kwSpanne(vonIso: string, bisIso: string): string | null {
  const t1 = tagTeile(vonIso); const t2 = tagTeile(bisIso);
  if (!t1 || !t2) return null;
  const a = `${t1[0]}-${t1[1]}-${t1[2]}`;
  const b = `${t2[0]}-${t2[1]}-${t2[2]}`;
  /* MONTAG BIS SONNTAG, UND GENAU 7 TAGE. Gerechnet ohne Uhr: `wochentagNr`
     zaehlt ab Montag, `tageAb` zaehlt Kalendertage und nicht Millisekunden -
     eine Woche ueber die Zeitumstellung hat 169 Stunden. */
  if (wochentagNr(a) !== 0 || wochentagNr(b) !== 6) return null;
  if (plusTage(a, 6) !== b) return null;
  return `KW ${kwVon(a)}/${kwJahrVon(a)} `
       + `(${datumKurz(vonIso)}-${datumKurz(bisIso)})`;
}

/**
 * Eine Uhrzeit als HH:MM.
 *
 * ZWEI EINGABEN, EINE ANTWORT. Aus der Datenbank kommt entweder eine reine
 * Uhrzeit („07:30:00") oder ein Zeitpunkt („2026-08-24T06:12:03.456Z"). Die
 * erste Fassung hat stur die ersten fünf Zeichen genommen; bei einem Zeitpunkt
 * stand dann „2026-" auf dem Bildschirm.
 */
export function uhr(w: string | Date | null | undefined): string {
  if (!w) return '';
  const s = String(w);
  if (/^\d{1,2}:\d{2}/.test(s)) return s.slice(0, 5);
  const d = w instanceof Date ? w : new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('de-DE',
    { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
}

export function menge(w: number | null | undefined, einheit = ''): string {
  if (w === null || w === undefined) return '';
  const s = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 }).format(w);
  return einheit ? `${s} ${einheit}` : s;
}

/* ============================================================================
 * GELD HAT IMMER 2 NACHKOMMASTELLEN
 * ============================================================================
 *
 * `menge()` darüber rundet auf 3 Stellen und lässt sie WEG, wenn sie 0 sind:
 * aus 1234,00 Euro wird „1.234". In einer Mengenspalte ist das richtig, in
 * einer Geldspalte nicht. **Eine Spalte, in der 1.234 und 1.234,56
 * untereinander stehen, liest man falsch** - und beim Vergleich zweier
 * Beträge ist genau das die Aufgabe.
 *
 * DAS ZEICHEN STEHT HINTEN UND MIT SCHMALEM ABSTAND, wie im deutschen Satz.
 * Eine negative Zahl behält ihr Minus davor: „-100,00 €" heisst hier, dass
 * die Telekom MEHR gutgeschrieben hat als wir eingereicht haben, und das ist
 * eine Auskunft und kein Schreibfehler.
 */
export function euro(w: number | string | null | undefined): string {
  if (w === null || w === undefined || w === '') return '';
  const n = typeof w === 'number' ? w : Number(w);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n) + '\u202f€';
}

export const ZUSTAND_TEXT: Record<string, string> = {
  neu: 'Neu',
  laufend: 'Läuft',
  ueberfaellig: 'Überfällig',
  unterbrochen: 'Unterbrochen',
  erledigt: 'Erledigt',
};

/* ============================================================================
 * WAS DIE MARKE „ÜBERFÄLLIG" MEINT
 * ============================================================================
 *
 * ORIGINALTEXT MARVIN (22.09.2026, mit 2 Karten aus der Arbeitsliste)
 *   „Warum stehen hier Aufträge auf «überfällig» wenn der Termin erst in in
 *    der Zukunft liegt? Da stimmt doch was nicht."
 *
 * NACHGEMESSEN WAR DIE REGEL RICHTIG: bei beiden Karten war der TIEFBAU der
 * Auslöser (einmal seit dem 14.09., einmal seit dem 25.08., beide offen),
 * und auf der Karte stehen Einblasen und Spleißen am 23.09. Der Tiefbau war
 * ein kleiner Chip in der letzten Zeile.
 *
 * **EINE MARKE, DIE EINE FRAGE AUFWIRFT STATT SIE ZU BEANTWORTEN, IST EINE
 * HALBE MARKE** - und diese Frage stellt sich jedem, der die Liste ansieht.
 * Sie steht deshalb hier und nicht in einem Bauteil: Liste und Akte zeigen
 * dieselbe Marke, und 2 Abschriften wären 2 Texte, sobald einer geändert
 * wird.
 */
export function zustandText(a: {
  zustand?: string | null;
  ueberfaellig_art?: string | null;
  ueberfaellig_seit?: string | null;
}): string {
  const grund = ZUSTAND_TEXT[a.zustand ?? ''] ?? (a.zustand ?? '');
  if (a.zustand !== 'ueberfaellig' || !a.ueberfaellig_art) return grund;
  /* DAS GEWERK AUSGESCHRIEBEN UND NICHT ALS KÜRZEL. „TB" steht auf dem
     Papier; hier liest ein Mensch. */
  const was = GEWERK_TEXT[a.ueberfaellig_art] ?? a.ueberfaellig_art;
  /* DAS DATUM UND NICHT DIE ANZAHL DER TAGE. „seit 25.08." steht morgen
     noch richtig da, „seit 28 Tagen" nicht - und die Marke landet auch in
     Meldungen und Ausfuhren. */
  return a.ueberfaellig_seit
    ? `${grund}: ${was} seit ${datumKurz(a.ueberfaellig_seit)}`
    : `${grund}: ${was}`;
}

export const GEWERK_TEXT: Record<string, string> = {
  TB: 'Tiefbau',
  EB: 'Einblasen',
  SP: 'Spleißen',
  DK: 'Doku',
};

/**
 * Die Werte von `dokument_art`, für Menschen.
 *
 * ORIGINALTEXT MARVIN (01.09.2026, zur Behördenmappe)
 *   „kannst du hier bei der „Art" die umlaute richtig schreiben."
 *
 * IN DER SPALTE STAND DER ROHE AUFZÄHLUNGSWERT: „GEBUEHRENBESCHEID",
 * „AUSNAHMEGENEHMIGUNG". Ein Aufzählungswert IST ohne Umlaut richtig - er
 * läuft durch CSV-Dateien, Dateipfade und fremde Schnittstellen, und dort
 * zerbricht ein Umlaut lautlos. **Was falsch war, ist nicht der Wert, sondern
 * dass er ungeschrieben auf den Bildschirm kam.** „Nur Bezeichner bleiben
 * ohne" heisst: der Bezeichner bleibt, und daneben steht der Text.
 *
 * Diese Liste ist deshalb VOLLSTÄNDIG über den Aufzählungstyp. Eine
 * halbierte Liste sieht aus wie eine ganze: die fehlenden Werte stehen roh
 * da, und es fällt nur dem auf, der genau diese Art vor sich hat. Bis heute
 * standen sechs davon in `AkteUebersicht.tsx` und keine in der Mappe.
 */
export const DOKUMENT_TEXT: Record<string, string> = {
  AAZ: 'Aufgrabungsanzeige',
  VAO: 'Verkehrsrechtliche Anordnung',
  FAZ: 'Fertigstellungsanzeige',
  POLLER: 'Pollerantrag',
  GEBUEHRENBESCHEID: 'Gebührenbescheid',
  AUSNAHMEGENEHMIGUNG: 'Ausnahmegenehmigung',
  RB: 'Rotberichtigung',
  BAUAKTE: 'Bauakte',
  LAGEPLAN: 'Lageplan',
  REGELPLAN: 'Regelplan',
  FOTO: 'Foto',
  EINBLASPROTOKOLL: 'Einblasprotokoll',
  MESSPROTOKOLL: 'Messprotokoll',
  CHECKLISTE: 'Checkliste',
  NACHWEIS: 'Nachweis',
  LEB: 'LEB',
  RECHNUNG: 'Rechnung',
  // Die Gutschrift der Telekom (Migration 099). AUSDRÜCKLICH NICHT
  // „Rechnung": das ist in diesem Haus die EINGEHENDE Rechnung eines
  // Subunternehmers - Geld, das hinaus geht.
  GUTSCHRIFT: 'Gutschrift der Telekom',
  SONSTIGES: 'Sonstiges',
};

/**
 * Der Text zu einer Art, und der ROHE Wert, wenn wir ihn nicht kennen.
 *
 * Eine unbekannte Art wird NICHT verschluckt: kommt mit einer Migration ein
 * Wert dazu, den diese Liste nicht kennt, steht er da, wie er ist - das fällt
 * auf und ist zu beheben. Ein leeres Feld fällt nicht auf.
 */
export function dokumentArt(w: unknown): string {
  const s = String(w ?? '').trim();
  return DOKUMENT_TEXT[s] ?? s;
}

// ---------------------------------------------------------- Auswahllisten
//
// Marvin, 23.08.2026: „achte dabei auch auf die details wie dropdown menüs und
// checkboxen, dass sowas mit übernommen wird, da wo es gebraucht wird."
//
// Die Werte stehen NICHT hier, sie kommen von /api/auswahl. Hier steht nur,
// welche Form sie haben, und ein Haken, damit sie einmal je Sitzung geholt
// werden statt in jedem Formular neu.

export interface Auswahl1 { wert: string; text: string }
export interface Gewerkelage {
  tiefbau: boolean; einblasen: boolean; spleissen: boolean; bekannt: boolean;
}
export interface Auswahllisten {
  auftragstyp: (Auswahl1 & { gewerke: Gewerkelage })[];
  prio: Auswahl1[];
  rohrverband: Auswahl1[];
  roehrchen: Auswahl1[];
  bearbeitungsstand: Auswahl1[];
  status_tk: Auswahl1[];
  auftragstyp_tk: Auswahl1[];
  tb_bereich: Auswahl1[];
}

let listenSpeicher: Promise<Auswahllisten> | null = null;

/**
 * Die Auswahllisten, einmal geholt.
 *
 * Sie aendern sich nur mit einer Lieferung. Sie in jedem Dialog neu zu holen
 * hiesse: bei jedem Oeffnen eines Formulars eine Anfrage, die immer dieselbe
 * Antwort gibt.
 */
export function auswahllisten(): Promise<Auswahllisten> {
  if (!listenSpeicher) listenSpeicher = api.auswahl();
  return listenSpeicher;
}

/** Welche Gewerke gehoeren zu diesem Auftragstyp. Rueckfall: alle drei. */
export function gewerkeVon(
  listen: Auswahllisten | null, typ: string | null | undefined
): Gewerkelage {
  const alle = { tiefbau: true, einblasen: true, spleissen: true, bekannt: false };
  if (!listen || !typ) return alle;
  return listen.auftragstyp.find((a) => a.wert === typ)?.gewerke ?? alle;
}


/* ============================================================================
 * „ZULETZT AKTIV" (.247): ein Klick oder Tastendruck meldet sich, höchstens
 * alle 2 Minuten und nur, wenn der Tab sichtbar ist. Was der Browser von
 * selbst abfragt, zählt nicht.
 * ==========================================================================*/
let aktivGemeldet = 0;
function aktivMelden(): void {
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
  const jetzt = Date.now();
  if (jetzt - aktivGemeldet < 120_000) return;
  aktivGemeldet = jetzt;
  /* ALS BEACON und nicht als `fetch`: kein Schreiben an Daten, das andere
     Bildschirme neu laden müssten (siehe `melde`), und es hält nichts auf. */
  try { navigator.sendBeacon('/api/aktiv'); } catch { /* dann eben nicht */ }
}
if (typeof window !== 'undefined') {
  for (const art of ['pointerdown', 'keydown'] as const) {
    window.addEventListener(art, aktivMelden, { passive: true, capture: true });
  }
}


/* MONTAGE VOR DEM TIEFBAU (.455). ORIGINALTEXT MARVIN (22.10.2026): „wenn ein
   Tiefbautermin abgesagt wird, muss unbedingt eine Meldung kommen, soll auch
   der Termin zum Einblasen abgesagt werden." Nach jeder Buchung und jedem
   Storno: meldet der Server einen Konflikt, fragt das Fenster sofort, ob die
   Montage mit abgesagt werden soll. „Abbrechen" lässt sie stehen; der Hinweis
   bleibt dann in der Glocke, bis es geklärt ist. */
/* FRAGEN STATT PUSHEN (.549). ORIGINALTEXT MARVIN (29.10.2026): „wir werden
   explizit gefragt, ob wir die Nachricht pushen wollen oder nicht ... Wenn wir
   eine Terminverschiebung haben, ist das nicht unbedingt wichtig, dass die
   Monteure wissen, dass was abgesagt wurde, was in der Vergangenheit lag."
   Eine Frage für jede Absage; liegt der Termin schon zurück, sagt sie es. */
export async function pushFrage(datum?: string | null, wer = 'Die Firma'): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const iso = String(datum ?? '').slice(0, 10);
  const heute = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const vorbei = /^\d{4}-\d{2}-\d{2}$/.test(iso) && iso < heute;
  return await fragen(
    (vorbei ? `Der abgesagte Termin (${iso.split('-').reverse().join('.')}) lag schon in der Vergangenheit; eine Meldung ist meist nicht nötig.\n\n` : '')
    + `${wer} per Telegram über die Absage informieren?\n\nOK = ja, pushen\nAbbrechen = nein, nicht pushen`);
}

async function montageFrage(r: any): Promise<any> {
  const k = r?.montage_konflikt;
  if (!k?.gewerke?.length || typeof window === 'undefined') return r;
  const ja = await fragen(`${k.text}\n\nOK = Montage jetzt absagen (die Firma bekommt die Absage)\nAbbrechen = stehen lassen, der Hinweis bleibt in der Glocke`);
  if (!ja) return r;
  try {
    const [erster, ...rest] = k.gewerke as string[];
    const grund = k.grund ?? (k.tiefbau ? `Tiefbau läuft bis ${String(k.tiefbau).split('-').reverse().join('.')}, Montage davor nicht möglich` : 'Tiefbau abgesagt');
    const s = await ruf<any>('/termine/' + erster + '/storno', { method: 'POST', body: JSON.stringify({ grund, gewerke: rest, telegram: await pushFrage(k.datum ?? null, 'Die Montagefirma') }) });
    return { ...r, montage_abgesagt: s?.storniert ?? 0 };
  } catch (f) {
    await melden(`Die Montage konnte nicht abgesagt werden: ${(f as any)?.fehler ?? f}`);
    return r;
  }
}
